import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./App.css";
import axios from "axios";

// --- Configuration ---
const API_URL = process.env.REACT_APP_API_URL || "";

// Debug API URL configuration
console.log("=== API URL DEBUG ===");
console.log("process.env.REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
console.log("Final API_URL:", API_URL);
console.log("=====================");

// Debug configuration - set to false for production
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

// Debug logging wrapper
function debugLog(...args) {
  if (DEBUG) {
    console.log(...args);
  }
}

// --- API Client ---
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 120 seconds for image uploads and long operations
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  
  // Debug request details
  console.log("=== API REQUEST DEBUG ===");
  console.log("Request URL:", config.url);
  console.log("Base URL:", config.baseURL);
  console.log("Full URL:", config.baseURL + config.url);
  console.log("========================");
  
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// --- Helper Functions ---
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

const stripHtmlTags = (html) => {
  if (!html) return '';
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const truncateText = (text, length = 50) => {
  if (!text) return '';
  // Strip HTML tags first
  const cleanText = stripHtmlTags(text);
  if (cleanText.length <= length) return cleanText;
  return cleanText.substring(0, length) + "...";
};

// CSV Export utility functions
const exportToCSV = (data, filename = 'products.csv', showError = null) => {
  if (!data || data.length === 0) {
    if (showError) showError('No data to export', 'Export Failed');
    return;
  }

  const headers = [
    'Name', 'SKU', 'Price', 'Status', 'Description', 
    'Date Added', 'Last Updated', 'WooCommerce URL', 
    'eBay URL', 'Etsy URL', 'Vinted URL'
  ];

  const csvContent = [
    headers.join(','),
    ...data.filter(product => product && typeof product === 'object').map(product => [
      `"${(product.name || '').replace(/"/g, '""')}"`,
      `"${product.sku || ''}"`,
      product.price || '0',
      `"${product.status || ''}"`,
      `"${(product.description || '').replace(/"/g, '""')}"`,
      `"${formatDate(product.date_added)}"`,
      `"${formatDate(product.last_updated)}"`,
      `"${product.listings?.woocommerce?.url || ''}"`,
      `"${product.listings?.ebay?.url || ''}"`,
      `"${product.listings?.etsy?.url || ''}"`,
      `"${product.listings?.vinted?.url || ''}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// CSV Import utility functions
const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
  const products = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
    if (values.length < headers.length) continue;

    const product = {};
    headers.forEach((header, index) => {
      const value = values[index] || '';
      
      switch (header) {
        case 'name':
          product.name = value;
          break;
        case 'sku':
          product.sku = value;
          break;
        case 'price':
          product.price = parseFloat(value) || 0;
          break;
        case 'description':
          product.description = value;
          break;
        case 'status':
          product.status = ['active', 'sold', 'action_required', 'inactive', 'delisted'].includes(value) ? value : 'active';
          break;
        case 'woocommerce url':
        case 'ebay url':
        case 'etsy url':
        case 'vinted url':
          const platform = header.split(' ')[0];
          if (!product.listings) product.listings = {};
          if (value) {
            product.listings[platform] = { url: value };
          }
          break;
      }
    });

    if (product.name && product.sku) {
      products.push(product);
    }
  }

  return products;
};

// --- Custom Hooks ---
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Alternative debounce that won't trigger re-renders during typing
const useDebouncedCallback = (callback, delay) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

// Keyboard shortcuts hook
const useKeyboardShortcuts = (shortcuts) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger shortcuts when typing in inputs
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;
      const alt = event.altKey;

      const shortcutKey = `${ctrl ? 'ctrl+' : ''}${shift ? 'shift+' : ''}${alt ? 'alt+' : ''}${key}`;
      
      if (shortcuts[shortcutKey]) {
        event.preventDefault();
        shortcuts[shortcutKey]();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

// --- Child Components ---

const CrosslistModal = React.memo(function CrosslistModal({ isOpen, onClose, product, bulkProducts, onCrosslist, showSuccess, showError, showWarning }) {
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPlatforms([]);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const platforms = [
    { key: 'woocommerce', name: 'WooCommerce', initial: 'W', color: '#7F54B3' },
    { key: 'ebay', name: 'eBay', initial: 'E', color: '#e53238' },
    { key: 'etsy', name: 'Etsy', initial: 'Et', color: '#f16521' },
    { key: 'vinted', name: 'Vinted', initial: 'V', color: '#09b1ba' }
  ];

  const existingListings = product?.listings || {};

  const handlePlatformToggle = (platformKey) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformKey) 
        ? prev.filter(p => p !== platformKey)
        : [...prev, platformKey]
    );
  };

  const handleCrosslist = async () => {
    if (selectedPlatforms.length === 0) {
      showWarning('Please select at least one platform to crosslist to.', 'Platform Required');
      return;
    }

    const productsToProcess = bulkProducts || [product];
    setIsProcessing(true);
    try {
      for (const prod of productsToProcess) {
        for (const platform of selectedPlatforms) {
          await onCrosslist(prod, platform);
        }
      }
      onClose();
    } catch (error) {
      // Crosslisting failed
    } finally {
      setIsProcessing(false);
    }
  };

  const resetAndClose = () => {
    setSelectedPlatforms([]);
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen || !product) return null;

  // Check which platforms already have listings

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && resetAndClose()}>
      <div className="modal-content" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>{bulkProducts ? `Crosslist ${bulkProducts.length} Products` : 'Crosslist Product'}</h2>
          <button onClick={resetAndClose} className="close-button" disabled={isProcessing}>
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          {bulkProducts ? (
            <div style={{ padding: '16px', backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px', marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                {bulkProducts.length} products selected for crosslisting
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                {bulkProducts.slice(0, 3).map(p => p.name).join(', ')}
                {bulkProducts.length > 3 && ` and ${bulkProducts.length - 3} more...`}
              </div>
            </div>
          ) : (
            <div className="product-info" style={{ padding: '16px', backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px', marginBottom: '20px' }}>
              <img
                src={product.images?.[0]?.src || "https://placehold.co/80x80/f9fafb/e5e7eb?text=N/A"}
                alt={product.name}
                className="product-image"
                style={{ width: '48px', height: '48px' }}
              />
              <div>
                <div className="product-name" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  {product.name}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  {formatCurrency(product.price)} • SKU: {product.sku}
                </div>
              </div>
            </div>
          )}

          <h4 style={{ marginBottom: '16px', color: 'var(--color-text-primary)', fontSize: '15px' }}>Select platforms to crosslist to:</h4>
          
          <div style={{ display: 'grid', gap: '12px' }}>
            {platforms.map((platform) => {
              const hasListing = existingListings[platform.key]?.url;
              const isDisabled = hasListing;
              
              return (
                <label
                  key={platform.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${
                      selectedPlatforms.includes(platform.key) 
                        ? platform.color 
                        : 'var(--color-border)'
                    }`,
                    backgroundColor: isDisabled 
                      ? 'var(--color-surface-tertiary)' 
                      : selectedPlatforms.includes(platform.key)
                        ? `${platform.color}15`
                        : 'var(--color-surface)',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(platform.key)}
                    onChange={() => !isDisabled && handlePlatformToggle(platform.key)}
                    disabled={isDisabled || isProcessing}
                    className="checkbox"
                    style={{ accentColor: platform.color }}
                  />
                  <div 
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '8px', 
                      backgroundColor: platform.color, 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '14px', 
                      fontWeight: '600' 
                    }}
                  >
                    {platform.initial}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>
                      {platform.name}
                    </div>
                    {hasListing && (
                      <div style={{ fontSize: '12px', color: 'var(--color-success)', marginTop: '2px' }}>
                        Already listed
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={resetAndClose} className="button-secondary" disabled={isProcessing}>
            Cancel
          </button>
          <button 
            onClick={handleCrosslist} 
            className="button-primary"
            disabled={selectedPlatforms.length === 0 || isProcessing}
          >
            {isProcessing ? 'Crosslisting...' : bulkProducts ? 
              `Crosslist ${bulkProducts.length} products to ${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}` :
              `Crosslist to ${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
});

const StatsCard = React.memo(function StatsCard({ title, value, change, changeType, icon, color }) {
  const changeColor =
    changeType === "positive"
      ? "#10B981"
      : changeType === "negative"
      ? "#EF4444"
      : "#6B7280";

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--border-radius-xl)",
        padding: "24px",
        boxShadow: "var(--shadow-md)",
        border: "1px solid var(--color-border)",
        position: "relative",
        overflow: "hidden",
      }}
    >

      <div>
        <h3
          style={{
            fontSize: "14px",
            fontWeight: "500",
            color: "var(--color-text-tertiary)",
            margin: "0 0 8px 0",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {title}
        </h3>

        <div
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "var(--color-text-primary)",
            margin: "0 0 8px 0",
          }}
        >
          {value}
        </div>

        <div
          style={{
            fontSize: "14px",
            color: changeColor,
            fontWeight: "500",
          }}
        >
          {change}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          height: "3px",
          background: color,
        }}
      />
    </div>
  );
});

const SimpleProductList = React.memo(function SimpleProductList({ products, activeTab }) {
  const productArray = Array.isArray(products) ? products : [];

  if (productArray.length === 0) {
    const messages = {
      sold: "You have no sold products yet.",
      inactive: "No inactive products. Products become inactive when delisted from all platforms.",
      action_required: "No products require action at this time.",
    };

    return (
      <div className="empty-state">
        <p className="empty-message">
          {messages[activeTab] || "You have no products in this category."}
        </p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="product-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th>Price</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {productArray.map((product) => (
            <tr key={product.id} className="product-row">
              <td>
                <div className="product-info">
                  <img
                    src={
                      product.images?.[0]?.src ||
                      "https://placehold.co/80x80/f9fafb/e5e7eb?text=N/A"
                    }
                    alt={product.name}
                    className="product-image"
                    loading="lazy"
                  />
                  <span className="product-name">{product.name}</span>
                </div>
              </td>
              <td>{product.sku}</td>
              <td>{formatCurrency(product.price)}</td>
              <td>
                <span
                  className={`status-pill ${product.status?.replace("_", "-")}`}
                >
                  {product.status?.replace("_", " ").toUpperCase()}
                </span>
              </td>
              <td>{formatDate(product.last_updated)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

function UpdateStatusModal({ products, onClose, onConfirm, isLoading }) {
  const [platforms, setPlatforms] = useState({
    woocommerce: true,
    ebay: true,
    etsy: true,
    vinted: false,
  });

  const handlePlatformChange = (platform) => {
    setPlatforms((prev) => ({ ...prev, [platform]: !prev[platform] }));
  };

  const getSelectedPlatforms = () => {
    return Object.keys(platforms).filter((p) => platforms[p]);
  };

  const handleDelist = () => {
    onConfirm("delist", getSelectedPlatforms());
    onClose();
  };

  const handleMarkAsSold = () => {
    onConfirm("mark_as_sold", getSelectedPlatforms());
    onClose();
  };

  const productNames = products.filter(p => p && typeof p === 'object' && p.name).map((p) => p.name).join(", ");
  const selectedPlatformsList = getSelectedPlatforms();

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>Delist Products</h2>
          <button
            onClick={onClose}
            className="close-button"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <p>
          Select platforms to delist: <strong>{truncateText(productNames, 60)}</strong>
        </p>
        
        <div style={{ 
          padding: '12px', 
          backgroundColor: 'var(--color-warning-light)', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid var(--color-warning)',
          fontSize: '14px'
        }}>
          ℹ️ <strong>Delist Only:</strong> Products will be set to inactive status and removed from selected platforms, but kept in your dashboard for relisting later.
        </div>

        <div className="platform-selection">
          <h4>Select platforms to delist from:</h4>
          {Object.entries(platforms).map(([platform, isSelected]) => (
            <label key={platform}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handlePlatformChange(platform)}
                disabled={isLoading}
              />
              {platform.charAt(0).toUpperCase() + platform.slice(1)}
            </label>
          ))}
        </div>

        {selectedPlatformsList.length === 0 && (
          <p className="warning-text">Please select at least one platform.</p>
        )}

        <div className="modal-actions">
          <button
            onClick={onClose}
            className="button-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleDelist}
            className="button-primary"
            disabled={isLoading || selectedPlatformsList.length === 0}
          >
            {isLoading ? "Delisting..." : "Delist from Selected Platforms"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ isOpen, onClose, onImport, showError }) {
  const [activeImportTab, setActiveImportTab] = useState('csv');
  const [csvData, setCsvData] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [duplicateHandling, setDuplicateHandling] = useState('skip'); // 'skip', 'update', 'merge'
  const [platformSyncStatus, setPlatformSyncStatus] = useState({});

  // Import from platforms
  const importFromPlatform = async (platform) => {
    setIsProcessing(true);
    setPlatformSyncStatus(prev => ({ ...prev, [platform]: 'syncing' }));
    
    try {
      const response = await apiClient.post(`/api/import/${platform}/`, {
        duplicate_handling: duplicateHandling
      });
      
      if (response.data.success) {
        setPlatformSyncStatus(prev => ({ ...prev, [platform]: 'success' }));
        // Add to notification queue
        if (onImport) {
          await onImport({
            type: 'platform_import',
            platform: platform,
            products: response.data.products,
            duplicatesFound: response.data.duplicates_found || 0,
            duplicatesHandled: response.data.duplicates_handled || 0,
            duplicateAction: duplicateHandling
          });
        }
      } else {
        setPlatformSyncStatus(prev => ({ ...prev, [platform]: 'error' }));
      }
    } catch (error) {
      console.error(`${platform} import failed:`, error);
      setPlatformSyncStatus(prev => ({ ...prev, [platform]: 'error' }));
    } finally {
      setIsProcessing(false);
    }
  };


  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvData(text);
      const parsed = parseCSV(text);
      setPreviewData(parsed.slice(0, 5)); // Show first 5 rows as preview
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'text/csv') {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleImport = async () => {
    if (!csvData) return;
    
    setIsProcessing(true);
    try {
      const products = parseCSV(csvData);
      await onImport(products);
      onClose();
      setCsvData('');
      setPreviewData([]);
    } catch (error) {
      showError('Import failed: ' + error.message, 'Import Error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh' }}>
        <div className="modal-header">
          <h2>Import Products</h2>
          <button onClick={onClose} className="close-button" disabled={isProcessing}>×</button>
        </div>
        
        {/* Duplicate Handling Settings */}
        <div style={{ padding: '0 24px', marginBottom: '20px', borderBottom: '1px solid var(--color-border)', paddingBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Duplicate SKU Handling</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { value: 'skip', label: 'Skip Duplicates', desc: 'Keep existing products unchanged' },
              { value: 'update', label: 'Update Existing', desc: 'Overwrite with imported data' },
              { value: 'merge', label: 'Merge Listings', desc: 'Combine platform listings' }
            ].map(option => (
              <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="duplicateHandling"
                  value={option.value}
                  checked={duplicateHandling === option.value}
                  onChange={(e) => setDuplicateHandling(e.target.value)}
                  style={{ margin: 0 }}
                />
                <div>
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Import Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '20px' }}>
          {[
            { id: 'csv', label: 'CSV File' },
            { id: 'platforms', label: 'Platform Sync' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveImportTab(tab.id)}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeImportTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                borderBottom: activeImportTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                fontWeight: activeImportTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ minHeight: '300px', padding: '0 24px', marginBottom: '20px' }}>
          {activeImportTab === 'csv' && (
            <div>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
                Expected CSV format: Name, SKU, Price, Status, Description, Date Added, Last Updated, WooCommerce URL, eBay URL, Etsy URL, Vinted URL
              </p>
          
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? '#4F46E5' : '#D1D5DB'}`,
              borderRadius: '8px',
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: dragOver ? '#F3F4F6' : '#FAFAFA',
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('csv-file-input').click()}
          >
            <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>Drop CSV file here or click to browse</p>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
          </div>

          {previewData.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4>Preview (first 5 rows):</h4>
            <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
              <table style={{ width: '100%', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>SKU</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Price</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((product, index) => (
                    <tr key={`preview-${product.sku || index}`}>
                      <td style={{ padding: '8px' }}>{product.name}</td>
                      <td style={{ padding: '8px' }}>{product.sku}</td>
                      <td style={{ padding: '8px' }}>{formatCurrency(product.price)}</td>
                      <td style={{ padding: '8px' }}>{product.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              {parseCSV(csvData).length} products found in CSV
            </p>
          </div>
          )}
            </div>
          )}

          {activeImportTab === 'platforms' && (
            <div>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
                Import products from connected platforms. Dashboard will be the source of truth - duplicates will be handled according to your settings above.
              </p>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                {[
                  { key: 'woocommerce', name: 'WooCommerce', color: '#7C3AED', desc: 'Import from your WooCommerce store' },
                  { key: 'ebay', name: 'eBay', color: '#0064D2', desc: 'Import from your eBay listings' },
                  { key: 'etsy', name: 'Etsy', color: '#F16521', desc: 'Import from your Etsy shop' }
                ].map(platform => (
                  <div key={platform.key} style={{ 
                    padding: '20px', 
                    border: '1px solid var(--color-border)', 
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-surface-secondary)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                          width: '12px', 
                          height: '12px', 
                          borderRadius: '50%', 
                          backgroundColor: platform.color 
                        }}></div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{platform.name}</h4>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>{platform.desc}</p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => importFromPlatform(platform.key)}
                        disabled={isProcessing || platformSyncStatus[platform.key] === 'syncing'}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 
                            platformSyncStatus[platform.key] === 'success' ? 'var(--color-success)' :
                            platformSyncStatus[platform.key] === 'error' ? 'var(--color-danger)' :
                            platformSyncStatus[platform.key] === 'syncing' ? 'var(--color-warning)' :
                            'var(--color-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: isProcessing ? 'not-allowed' : 'pointer',
                          minWidth: '80px'
                        }}
                      >
                        {platformSyncStatus[platform.key] === 'syncing' ? 'Syncing...' :
                         platformSyncStatus[platform.key] === 'success' ? 'Success' :
                         platformSyncStatus[platform.key] === 'error' ? 'Error' :
                         'Import'}
                      </button>
                    </div>
                    
                    {platformSyncStatus[platform.key] === 'error' && (
                      <p style={{ 
                        fontSize: '12px', 
                        color: 'var(--color-danger)', 
                        margin: 0,
                        fontStyle: 'italic'
                      }}>
                        Import failed. Check your connection settings.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="button-secondary" disabled={isProcessing}>
            Cancel
          </button>
          {activeImportTab === 'csv' && (
            <button 
              onClick={handleImport} 
              className="button-primary"
              disabled={!csvData || isProcessing}
            >
              {isProcessing ? 'Importing...' : 'Import Products'}
            </button>
          )}
          
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ isOpen, onClose, showSuccess, showError, showWarning, selectedPolicies, setSelectedPolicies, platformSettings, setPlatformSettings, sourcePlatform, setSourcePlatform }) {
  const [activeTab, setActiveTab] = useState("connections");
  const [creds, setCreds] = useState({
    woo_url: "",
    woo_ck: "",
    woo_cs: "",
    pricing_rules: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [ebayPolicies, setEbayPolicies] = useState(null);
  const [etsyProfiles, setEtsyProfiles] = useState(null);
  const [isEbayPoliciesLoading, setIsEbayPoliciesLoading] = useState(false);
  const [isEtsyPoliciesLoading, setIsEtsyPoliciesLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState(null);
  const [ebayCategories, setEbayCategories] = useState([]);
  const [defaultEbayCategory, setDefaultEbayCategory] = useState('');

  // Fetch eBay categories
  const fetchEbayCategories = async () => {
    try {
      const response = await apiClient.get('/api/ebay-categories/');
      console.log('eBay categories response:', response.data);
      
      if (response.data.success) {
        const categories = response.data.categories || [];
        console.log(`Loaded ${categories.length} eBay categories from ${response.data.source || 'unknown source'}`);
        setEbayCategories(categories);
        
        if (response.data.message) {
          console.log('eBay categories message:', response.data.message);
        }
      } else {
        console.error('eBay categories API error:', response.data.error);
        if (response.data.debug_info) {
          console.error('Debug info:', response.data.debug_info);
        }
        setEbayCategories([]);
      }
    } catch (error) {
      console.error('Failed to fetch eBay categories:', error);
      setEbayCategories([]); // Set empty array on error
    }
  };

  // Fetch eBay policies
  const fetchEbayPolicies = async (skipLoading = false) => {
    if (!skipLoading) setIsEbayPoliciesLoading(true);
    try {
      const response = await apiClient.get('/api/ebay-policies/');
      
      if (response.data.success) {
        setEbayPolicies(response.data);
        // Store in localStorage for persistence
        localStorage.setItem('ebayPolicies', JSON.stringify(response.data));
        
        // Load saved policy selections if available
        if (response.data.current_settings) {
          setSelectedPolicies(prev => ({
            ...prev,
            ...response.data.current_settings
          }));
        }
      } else {
        // Handle the case where eBay is not connected
        setEbayPolicies(response.data); // Still set it so UI can show the message
        localStorage.setItem('ebayPolicies', JSON.stringify(response.data));
        if (response.data.error.includes('not connected')) {
          showWarning('eBay account not connected. Please connect your eBay account first to configure policies.', 'eBay Not Connected');
        } else {
          showError(response.data.error, 'Policy Loading Error');
        }
      }
    } catch (error) {
      console.error('Failed to fetch eBay policies:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load eBay policies';
      showError(errorMessage, 'Policy Loading Error');
    } finally {
      if (!skipLoading) setIsEbayPoliciesLoading(false);
    }
  };

  // Fetch Etsy shipping profiles
  const fetchEtsyProfiles = async (skipLoading = false) => {
    if (!skipLoading) setIsEtsyPoliciesLoading(true);
    try {
      const response = await apiClient.get('/api/etsy-profiles/');
      
      if (response.data.success) {
        setEtsyProfiles(response.data);
        // Store in localStorage for persistence
        localStorage.setItem('etsyProfiles', JSON.stringify(response.data));
        
        // Load saved Etsy settings if available
        if (response.data.current_settings) {
          setSelectedPolicies(prev => ({
            ...prev,
            etsyShipping: response.data.current_settings.shipping_profile || '',
            etsyReturn: response.data.current_settings.return_policy || ''
          }));
        }
      } else {
        setEtsyProfiles(response.data);
        localStorage.setItem('etsyProfiles', JSON.stringify(response.data));
        if (response.data.error.includes('not connected')) {
          showWarning('Etsy account not connected. Please connect your Etsy account first to configure profiles.', 'Etsy Not Connected');
        } else {
          showError(response.data.error, 'Profile Loading Error');
        }
      }
    } catch (error) {
      console.error('Failed to fetch Etsy profiles:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load Etsy profiles';
      showError(errorMessage, 'Profile Loading Error');
    } finally {
      if (!skipLoading) setIsEtsyPoliciesLoading(false);
    }
  };

  // Fetch both eBay and Etsy policies simultaneously
  const fetchAllPolicies = async () => {
    setIsEbayPoliciesLoading(true);
    setIsEtsyPoliciesLoading(true);
    try {
      await Promise.all([
        fetchEbayPolicies(true),
        fetchEtsyProfiles(true)
      ]);
      showSuccess('All policies reloaded successfully!', 'Policies Updated');
    } catch (error) {
      console.error('Failed to fetch all policies:', error);
      showError('Failed to reload some policies. Check individual platform connections.', 'Policy Loading Error');
    } finally {
      setIsEbayPoliciesLoading(false);
      setIsEtsyPoliciesLoading(false);
    }
  };


  const fetchCredentials = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/api/credentials/");
      setCreds(response.data);
      
      // Load default eBay category
      setDefaultEbayCategory(response.data.default_ebay_category || '');
      
      // Check if platforms are already connected
      const newTestResults = {};
      if (response.data.ebay_token) {
        newTestResults.ebay = "connected";
      }
      if (response.data.etsy_token && response.data.etsy_shop_id) {
        newTestResults.etsy = "connected";
      }
      setTestResults(prev => ({ ...prev, ...newTestResults }));
      
    } catch (error) {
      console.error("Failed to fetch credentials:", error);
      setCreds({ woo_url: "", woo_ck: "", woo_cs: "", pricing_rules: {} });
    } finally {
      setIsLoading(false);
    }
  };

  // Load saved policy settings
  const loadSavedPolicies = async () => {
    try {
      const response = await apiClient.get('/api/credentials/');
      if (response.data && response.data.pricing_rules) {
        const ebaySettings = response.data.pricing_rules.ebay_settings || {};
        const etsySettings = response.data.pricing_rules.etsy_settings || {};
        
        console.log('DEBUG: Loading policies from credentials:', ebaySettings, etsySettings);
        setSelectedPolicies(prev => ({
          ...prev,
          ebayPayment: ebaySettings.payment_policy || '',
          ebayShipping: ebaySettings.shipping_policy || '',
          ebayReturn: ebaySettings.return_policy || '',
          etsyShipping: etsySettings.shipping_profile || '',
          etsyReturn: etsySettings.return_policy || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load saved policies:', error);
      // Don't show error to user as this is just for loading defaults
    }
  };

  // Load stored policy data from localStorage on modal open
  const loadStoredPolicies = () => {
    try {
      const storedEbayPolicies = localStorage.getItem('ebayPolicies');
      const storedEtsyProfiles = localStorage.getItem('etsyProfiles');
      
      if (storedEbayPolicies) {
        const ebayData = JSON.parse(storedEbayPolicies);
        setEbayPolicies(ebayData);
        console.log('DEBUG: Loaded eBay policies from localStorage');
      }
      
      if (storedEtsyProfiles) {
        const etsyData = JSON.parse(storedEtsyProfiles);
        setEtsyProfiles(etsyData);
        console.log('DEBUG: Loaded Etsy profiles from localStorage');
      }
    } catch (error) {
      console.error('Failed to load stored policies:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      try {
        fetchCredentials();
        fetchEbayCategories();
        loadSavedPolicies();
        loadStoredPolicies(); // Load from localStorage instead of fetching from API
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error initializing settings modal:', error);
      }
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCreds((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
    if (testResults[name]) {
      setTestResults((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handlePolicyChange = (field, value) => {
    setSelectedPolicies((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setResetConfirmation({
        type: 'close',
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close without saving?',
        isDestructive: false
      });
    } else {
      onClose();
    }
  };

  const testWooConnection = async () => {
    if (!creds.woo_url || !creds.woo_ck || !creds.woo_cs) {
      showWarning("Please fill in all WooCommerce fields first.", 'Missing Information');
      return;
    }

    try {
      setTestResults((prev) => ({ ...prev, woocommerce: "testing" }));
      const response = await apiClient.post("/api/test-connection/", {
        platform: "woocommerce",
        credentials: {
          url: creds.woo_url,
          consumer_key: creds.woo_ck,
          consumer_secret: creds.woo_cs,
        },
      });
      setTestResults((prev) => ({ ...prev, woocommerce: "success" }));
    } catch (error) {
      setTestResults((prev) => ({ ...prev, woocommerce: "error" }));
      console.error("WooCommerce connection test failed:", error);
    }
  };

  const connectEbay = async () => {
    try {
      setTestResults((prev) => ({ ...prev, ebay: "connecting" }));
      
      // Get eBay OAuth authorization URL
      const response = await apiClient.get("/api/oauth/ebay/init/");
      
      if (response.data.auth_url) {
        // Open OAuth flow in new window
        const authWindow = window.open(
          response.data.auth_url,
          'ebay_oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Monitor for OAuth completion
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            setTestResults((prev) => ({ ...prev, ebay: "connected" }));
            // Refresh credentials to get updated connection status
            fetchCredentials();
          }
        }, 1000);
        
        // Handle OAuth completion message
        const handleMessage = async (event) => {
          // Security: Only accept messages from our OAuth redirect domain
          if (!event.origin.includes('github.io')) return;
          
          if (event.data.type === 'oauth_success' && event.data.platform === 'ebay') {
            authWindow.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            
            try {
              // Automatically complete OAuth with the received code
              const oauthResponse = await apiClient.post("/api/oauth/ebay/manual/", {
                code: event.data.code
              });
              
              
              if (oauthResponse.data.success) {
                setTestResults((prev) => ({ ...prev, ebay: "connected" }));
                showSuccess('eBay account connected successfully!', 'Connection Successful');
                fetchCredentials();
              }
            } catch (error) {
              setTestResults((prev) => ({ ...prev, ebay: "error" }));
              showError(`eBay connection failed: ${error.response?.data?.error || error.message}`, 'eBay Connection Error');
            }
          } else if (event.data.type === 'oauth_error' && event.data.platform === 'ebay') {
            authWindow.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setTestResults((prev) => ({ ...prev, ebay: "error" }));
            showError(`eBay OAuth failed: ${event.data.error}`, 'OAuth Failed');
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Reset state if user closes window manually
        setTimeout(() => {
          if (authWindow.closed) {
            setTestResults((prev) => ({ ...prev, ebay: "" }));
          }
        }, 300000); // 5 minute timeout
        
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      setTestResults((prev) => ({ ...prev, ebay: "error" }));
      showError(`eBay connection failed: ${error.response?.data?.error || error.message}`, 'eBay Connection Error');
    }
  };

  const disconnectEbay = async () => {
    try {
      setTestResults((prev) => ({ ...prev, ebay: "connecting" }));
      const response = await apiClient.post("/api/oauth/ebay/disconnect/");
      if (response.data.success) {
        setTestResults((prev) => ({ ...prev, ebay: "" }));
        showSuccess('eBay account disconnected successfully!', 'Account Disconnected');
        fetchCredentials();
      }
    } catch (error) {
      setTestResults((prev) => ({ ...prev, ebay: "error" }));
      showError(`eBay disconnect failed: ${error.response?.data?.error || error.message}`, 'Disconnect Failed');
    }
  };

  const disconnectEtsy = async () => {
    try {
      setTestResults((prev) => ({ ...prev, etsy: "connecting" }));
      const response = await apiClient.post("/api/oauth/etsy/disconnect/");
      if (response.data.success) {
        setTestResults((prev) => ({ ...prev, etsy: "" }));
        showSuccess('Etsy account disconnected successfully!', 'Account Disconnected');
        fetchCredentials();
      }
    } catch (error) {
      setTestResults((prev) => ({ ...prev, etsy: "error" }));
      showError(`Etsy disconnect failed: ${error.response?.data?.error || error.message}`, 'Disconnect Failed');
    }
  };

  const connectEtsy = async () => {
    try {
      setTestResults((prev) => ({ ...prev, etsy: "connecting" }));
      
      // Get Etsy OAuth authorization URL
      const response = await apiClient.get(`/api/oauth/etsy/init/?t=${Date.now()}`);
      
      if (response.data.auth_url) {
        // Open OAuth flow in new window
        const authWindow = window.open(
          response.data.auth_url,
          'etsy_oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Monitor for OAuth completion
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            setTestResults((prev) => ({ ...prev, etsy: "connected" }));
            // Refresh credentials to get updated connection status
            fetchCredentials();
          }
        }, 1000);
        
        // Handle OAuth completion message
        const handleMessage = async (event) => {
          // Security: Only accept messages from our OAuth redirect domain
          if (!event.origin.includes('github.io')) return;
          
          if (event.data.type === 'oauth_success' && event.data.platform === 'etsy') {
            authWindow.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            
            try {
              // Automatically complete OAuth with the received code
              const oauthResponse = await apiClient.post("/api/oauth/etsy/manual/", {
                code: event.data.code
              });
              
              if (oauthResponse.data.success) {
                setTestResults((prev) => ({ ...prev, etsy: "connected" }));
                showSuccess('Etsy account connected successfully!', 'Connection Successful');
                fetchCredentials();
              }
            } catch (error) {
              setTestResults((prev) => ({ ...prev, etsy: "error" }));
              showError(`Etsy connection failed: ${error.response?.data?.error || error.message}`, 'Connection Failed');
            }
          } else if (event.data.type === 'oauth_error' && event.data.platform === 'etsy') {
            authWindow.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setTestResults((prev) => ({ ...prev, etsy: "error" }));
            showError(`Etsy OAuth failed: ${event.data.error}`, 'OAuth Failed');
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Reset state if user closes window manually
        setTimeout(() => {
          if (authWindow.closed) {
            setTestResults((prev) => ({ ...prev, etsy: "" }));
          }
        }, 300000); // 5 minute timeout
        
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      setTestResults((prev) => ({ ...prev, etsy: "error" }));
      showError(`Etsy connection failed: ${error.response?.data?.error || error.message}`, 'Connection Failed');
    }
  };



  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Prepare credentials with embedded policy settings
      const updatedCreds = {
        ...creds,
        default_ebay_category: defaultEbayCategory,
        pricing_rules: {
          ...creds.pricing_rules,
          ebay_settings: {
            ...creds.pricing_rules?.ebay_settings,
            payment_policy: selectedPolicies?.ebayPayment || '',
            shipping_policy: selectedPolicies?.ebayShipping || '',
            return_policy: selectedPolicies?.ebayReturn || ''
          },
          etsy_settings: {
            ...creds.pricing_rules?.etsy_settings,
            shipping_profile: selectedPolicies?.etsyShipping || '',
            return_policy: selectedPolicies?.etsyReturn || ''
          }
        }
      };
      
      // Save everything in one API call
      console.log('DEBUG: Saving policies:', updatedCreds.pricing_rules);
      await apiClient.put("/api/credentials/", updatedCreds);
      
      showSuccess('Settings saved successfully!', 'Settings Saved');
      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      showError("Failed to save settings.", 'Save Failed');
      console.error("Save settings error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSalesData = (resetType) => {
    const confirmMessages = {
      today: "Are you sure you want to reset today's sales data? This will clear sale dates for products sold today.",
      monthly: "Are you sure you want to reset this month's sales data? This will clear sale dates for all products sold this month.",
      total: "Are you sure you want to reset ALL sales data? This will clear sale dates for all sold products."
    };

    setResetConfirmation({
      type: resetType,
      title: 'Reset Sales Data',
      message: confirmMessages[resetType],
      isDestructive: true
    });
  };

  const confirmResetSalesData = async () => {
    if (!resetConfirmation) return;
    
    if (resetConfirmation.type === 'close') {
      setHasUnsavedChanges(false);
      onClose();
      setResetConfirmation(null);
      return;
    }
    
    try {
      const response = await apiClient.post('/api/products/reset_sales_data/', {
        reset_type: resetConfirmation.type
      });

      if (response.data.count === 0) {
        showWarning(`No ${resetConfirmation.type === 'today' ? "today's" : resetConfirmation.type === 'monthly' ? "this month's" : ""} sales data to reset.`, 'No Data');
      } else {
        showSuccess(response.data.message, 'Sales Data Reset');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to reset sales data';
      showError(errorMessage, 'Reset Failed');
      console.error('Reset sales data error:', error);
    }
    
    setResetConfirmation(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="modal-content" style={{ 
        maxWidth: '90vw', 
        maxHeight: '85vh', 
        width: '1000px',
        height: '700px',
        padding: '0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
          <h2>Settings</h2>
          <button
            onClick={handleClose}
            className="close-button"
            disabled={isSaving}
          >
            ×
          </button>
        </div>
        
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left Sidebar with Tabs */}
          <div style={{ 
            width: '220px', 
            backgroundColor: 'var(--color-surface-secondary)', 
            borderRight: '1px solid var(--color-border)',
            padding: '20px 0',
            flexShrink: 0
          }}>
            <div style={{ padding: '0 20px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Configuration
              </h3>
            </div>
            
            <div>
              {[
                { id: 'connections', label: 'Connections' },
                { id: 'platforms', label: 'Platform Visibility' },
                { id: 'source', label: 'Source Platform' },
                { id: 'pricing', label: 'Pricing Rules' },
                { id: 'policies', label: 'Default Policies' },
                { id: 'data', label: 'Sales Data' },
                { id: 'account', label: 'Account' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: activeTab === tab.id ? 'var(--color-primary)' : 'transparent',
                    color: activeTab === tab.id ? 'white' : 'var(--color-text-primary)',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.backgroundColor = 'var(--color-surface-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Right Content Area */}
          <div style={{ flex: 1, padding: '30px', overflow: 'auto' }}>

            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <div className="loading-spinner"></div>
                <p>Loading settings...</p>
              </div>
            ) : (
              <>
                {activeTab === 'connections' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Platform Connections</h3>
                    
                    <div className="settings-section">
              <h3>WooCommerce Connection</h3>
              <div className="form-group">
                <label htmlFor="woo_url">Store URL</label>
                <input
                  id="woo_url"
                  type="url"
                  name="woo_url"
                  value={creds.woo_url}
                  onChange={handleChange}
                  placeholder="https://yourstore.com"
                  disabled={isSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="woo_ck">Consumer Key</label>
                <input
                  id="woo_ck"
                  type="password"
                  name="woo_ck"
                  value={creds.woo_ck}
                  onChange={handleChange}
                  placeholder="ck_xxxxxxxxxxxx"
                  disabled={isSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="woo_cs">Consumer Secret</label>
                <input
                  id="woo_cs"
                  type="password"
                  name="woo_cs"
                  value={creds.woo_cs}
                  onChange={handleChange}
                  placeholder="cs_xxxxxxxxxxxx"
                  disabled={isSaving}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <button
                  type="button"
                  onClick={testWooConnection}
                  className="button-secondary"
                  disabled={isSaving || testResults.woocommerce === "testing"}
                  style={{ width: "100%" }}
                >
                  {testResults.woocommerce === "testing" ? (
                    <>
                      <span
                        className="loading-spinner"
                        style={{ marginRight: "8px" }}
                      ></span>
                      Testing Connection...
                    </>
                  ) : (
                    "Test WooCommerce Connection"
                  )}
                </button>

                {testResults.woocommerce === "success" && (
                  <p
                    style={{
                      color: "var(--color-success)",
                      marginTop: "8px",
                      fontSize: "14px",
                    }}
                  >
                    ✓ Connection successful!
                  </p>
                )}

                {testResults.woocommerce === "error" && (
                  <p
                    style={{
                      color: "var(--color-danger)",
                      marginTop: "8px",
                      fontSize: "14px",
                    }}
                  >
                    ✗ Connection failed. Please check your credentials.
                  </p>
                )}
              </div>
            </div>

            <div className="settings-section">
              <h3>Other Platforms</h3>
              <p
                style={{
                  color: "var(--color-gray-500)",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Connect your other marketplace accounts to enable cross-listing
                and automated management.
              </p>

              {/* eBay OAuth Section */}
              <div style={{ marginBottom: "24px", padding: "16px", backgroundColor: "var(--color-surface-secondary)", borderRadius: "8px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "16px" }}>eBay Connection</h4>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={connectEbay}
                    className="button-secondary"
                    disabled={isSaving || testResults.ebay === "connecting"}
                    style={{ minWidth: "140px" }}
                  >
                    {testResults.ebay === "connecting" ? (
                      <>
                        <span className="loading-spinner" style={{ marginRight: "8px" }}></span>
                        Connecting...
                      </>
                    ) : testResults.ebay === "connected" ? (
                      "eBay Connected ✓"
                    ) : (
                      "Connect eBay Account"
                    )}
                  </button>
                  {testResults.ebay === "connected" && (
                    <button
                      type="button"
                      onClick={disconnectEbay}
                      className="button-secondary"
                      disabled={isSaving || testResults.ebay === "connecting"}
                      style={{ minWidth: "120px", marginLeft: "8px" }}
                    >
                      Disconnect eBay
                    </button>
                  )}
                  
                </div>
              </div>

              {/* Etsy OAuth Section */}
              <div style={{ marginBottom: "16px", padding: "16px", backgroundColor: "var(--color-surface-secondary)", borderRadius: "8px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "16px" }}>Etsy Connection</h4>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={connectEtsy}
                    className="button-secondary"
                    disabled={isSaving || testResults.etsy === "connecting"}
                    style={{ minWidth: "140px" }}
                  >
                    {testResults.etsy === "connecting" ? (
                      <>
                        <span className="loading-spinner" style={{ marginRight: "8px" }}></span>
                        Connecting...
                      </>
                    ) : testResults.etsy === "connected" ? (
                      "Etsy Connected ✓"
                    ) : (
                      "Connect Etsy Account"
                    )}
                  </button>
                  {testResults.etsy === "connected" && (
                    <button
                      type="button"
                      onClick={disconnectEtsy}
                      className="button-secondary"
                      disabled={isSaving || testResults.etsy === "connecting"}
                      style={{ minWidth: "120px", marginLeft: "8px" }}
                    >
                      Disconnect Etsy
                    </button>
                  )}
                  
                </div>
              </div>
                    </div>
                  </div>
                )}

                {activeTab === 'platforms' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Platform Visibility</h3>
                    <p style={{ marginBottom: '30px', color: 'var(--color-text-secondary)', fontSize: '16px' }}>
                      Control which platforms appear in tables, dropdowns, and crosslisting menus. Disabled platforms will be hidden from the interface.
                    </p>
                    
                    <div className="settings-section">
                      <div style={{ display: 'grid', gap: '20px' }}>
                        {Object.entries(platformSettings).map(([key, platform]) => (
                          <div key={key} style={{ 
                            padding: '20px', 
                            border: '1px solid var(--color-border)', 
                            borderRadius: '8px',
                            backgroundColor: 'var(--color-surface-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                borderRadius: '50%', 
                                backgroundColor: platform.enabled ? '#10B981' : '#EF4444' 
                              }}></div>
                              <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                  {platform.name}
                                </h4>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                                  {platform.enabled ? 'Visible in interface' : 'Hidden from interface'}
                                </p>
                              </div>
                            </div>
                            <label style={{ 
                              position: 'relative', 
                              display: 'inline-block', 
                              width: '48px', 
                              height: '24px' 
                            }}>
                              <input
                                type="checkbox"
                                checked={platform.enabled}
                                onChange={(e) => setPlatformSettings(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], enabled: e.target.checked }
                                }))}
                                style={{ opacity: 0, width: 0, height: 0 }}
                              />
                              <span style={{
                                position: 'absolute',
                                cursor: 'pointer',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: platform.enabled ? '#10B981' : '#D1D5DB',
                                transition: '0.4s',
                                borderRadius: '24px'
                              }}></span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'source' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Source Platform</h3>
                    <p style={{ marginBottom: '30px', color: 'var(--color-text-secondary)', fontSize: '16px' }}>
                      Choose your primary platform. When syncing products that already exist, the source platform determines which fields (title, description, price, images) are preserved, while other platforms only update their listing URLs.
                    </p>
                    
                    <div style={{ 
                      display: 'grid', 
                      gap: '20px',
                      backgroundColor: 'var(--color-surface)',
                      padding: '24px',
                      borderRadius: 'var(--border-radius-lg)',
                      border: '1px solid var(--color-border)'
                    }}>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '12px', 
                          fontSize: '16px', 
                          fontWeight: '600',
                          color: 'var(--color-text-primary)'
                        }}>
                          Source Platform
                        </label>
                        <select
                          value={sourcePlatform}
                          onChange={(e) => setSourcePlatform(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--border-radius)',
                            fontSize: '14px',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text-primary)'
                          }}
                        >
                          <option value="woocommerce">WooCommerce</option>
                          <option value="ebay">eBay</option>
                          <option value="etsy">Etsy</option>
                          <option value="vinted">Vinted</option>
                        </select>
                        <div style={{
                          marginTop: '8px',
                          fontSize: '13px',
                          color: 'var(--color-text-secondary)',
                          lineHeight: '1.4'
                        }}>
                          Products imported from <strong>{sourcePlatform === 'woocommerce' ? 'WooCommerce' : sourcePlatform === 'ebay' ? 'eBay' : sourcePlatform === 'etsy' ? 'Etsy' : 'Vinted'}</strong> will be treated as the master copy. 
                          When syncing existing products, only listing URLs will be updated from other platforms.
                        </div>
                      </div>

                      <div style={{
                        padding: '16px',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        <div style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>
                          <strong>How it works:</strong>
                          <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                            <li>New products: Create with all details from any platform</li>
                            <li>Existing products from source: Update all fields (title, price, description, images)</li>
                            <li>Existing products from other platforms: Only update listing URLs</li>
                            <li>Setting persists across browser sessions and logins</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'pricing' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Pricing Rules</h3>
                    
                    <div className="settings-section">
              <h3>Platform Markup Settings</h3>
              <p
                style={{
                  color: "var(--color-gray-500)",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Set percentage markup for each platform when cross-listing products.
              </p>

              <div style={{ display: "grid", gap: "16px" }}>
                {[
                  { key: 'woocommerce', name: 'WooCommerce', color: '#7C3AED', isSource: true },
                  { key: 'ebay', name: 'eBay', color: '#0064D2' },
                  { key: 'etsy', name: 'Etsy', color: '#F16521' },
                  { key: 'vinted', name: 'Vinted', color: '#008080' }
                ].filter(platform => platformSettings?.[platform.key]?.enabled !== false).map(platform => (
                  <div key={platform.key} style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    padding: "12px", 
                    backgroundColor: "var(--color-surface-secondary)", 
                    borderRadius: "8px",
                    border: "1px solid var(--color-border)"
                  }}>
                    <div style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: platform.color,
                      borderRadius: "50%",
                      marginRight: "12px"
                    }}></div>
                    <label 
                      htmlFor={`pricing_${platform.key}`}
                      style={{ 
                        flex: 1, 
                        fontWeight: "500",
                        fontSize: "14px"
                      }}
                    >
                      {platform.name}
                    </label>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {platform.isSource ? (
                        <span style={{
                          fontSize: "13px",
                          color: "var(--color-text-secondary)",
                          fontStyle: "italic",
                          backgroundColor: "var(--color-gray-100)",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          border: "1px solid var(--color-border)"
                        }}>
                          Source Platform
                        </span>
                      ) : (
                        <>
                          <input
                            id={`pricing_${platform.key}`}
                            type="number"
                            min="0"
                            max="1000"
                            step="1"
                            value={creds.pricing_rules?.[`${platform.key}_markup`] || 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setCreds(prev => ({
                                ...prev,
                                pricing_rules: {
                                  ...prev.pricing_rules,
                                  [`${platform.key}_markup`]: value
                                }
                              }));
                            }}
                            disabled={isSaving}
                            style={{
                              width: "80px",
                              padding: "6px 8px",
                              border: "1px solid var(--color-border)",
                              borderRadius: "4px",
                              textAlign: "center",
                              fontSize: "14px"
                            }}
                          />
                          <span style={{ 
                            fontSize: "14px", 
                            color: "var(--color-text-secondary)",
                            minWidth: "20px"
                          }}>
                            %
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                marginTop: "20px", 
                padding: "16px", 
                backgroundColor: "var(--color-surface-secondary)", 
                borderRadius: "8px",
                border: "1px solid var(--color-border)"
              }}>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}>
                  <input
                    type="checkbox"
                    checked={creds.pricing_rules?.round_to_99 || false}
                    onChange={(e) => {
                      setCreds(prev => ({
                        ...prev,
                        pricing_rules: {
                          ...prev.pricing_rules,
                          round_to_99: e.target.checked
                        }
                      }));
                    }}
                    disabled={isSaving}
                    style={{ 
                      width: "16px", 
                      height: "16px",
                      accentColor: "var(--color-primary)"
                    }}
                  />
                  <span>Round prices to nearest .99</span>
                </label>
                <p style={{ 
                  fontSize: "12px", 
                  color: "var(--color-text-secondary)",
                  margin: "8px 0 0 28px"
                }}>
                  When enabled, calculated prices will be rounded to end in .99 (e.g., £12.50 becomes £12.99)
                </p>
              </div>
              
              <div style={{ 
                marginTop: "16px", 
                padding: "12px", 
                backgroundColor: "var(--color-info-bg, #EBF8FF)", 
                border: "1px solid var(--color-info-border, #BEE3F8)",
                borderRadius: "6px" 
              }}>
                <p style={{ 
                  fontSize: "13px", 
                  color: "var(--color-info-text, #2B6CB0)",
                  margin: 0 
                }}>
                  <strong>Example:</strong> If you set eBay to 25%, a product priced at £10 will be listed on eBay for £12.50. With "Round to .99" enabled, it becomes £12.99.
                </p>
              </div>
                    </div>
                  </div>
                )}
                {activeTab === 'policies' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Default Policies</h3>
                    
                    {/* eBay Policies Section */}
                    <div className="settings-section" style={{ marginBottom: '32px', padding: '20px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                      <h4 style={{ marginBottom: '16px', color: 'var(--color-text)', fontSize: '18px', fontWeight: '600' }}>eBay Business Policies</h4>
                      
                      {isEbayPoliciesLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="loading-spinner"></span>
                          <span>Loading eBay policies...</span>
                        </div>
                      ) : ebayPolicies && ebayPolicies.success ? (
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {/* Payment Policy */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Payment Policy:</label>
                            <select 
                              value={selectedPolicies.ebayPayment} 
                              onChange={(e) => handlePolicyChange('ebayPayment', e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                border: '1px solid var(--color-border)', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                              }}
                            >
                              <option value="">Select a payment policy</option>
                              {ebayPolicies.payment_policies?.map(policy => (
                                <option key={policy.id} value={policy.name}>
                                  {policy.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Shipping Policy */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Shipping Policy:</label>
                            <select 
                              value={selectedPolicies.ebayShipping} 
                              onChange={(e) => handlePolicyChange('ebayShipping', e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                border: '1px solid var(--color-border)', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                              }}
                            >
                              <option value="">Select a shipping policy</option>
                              {ebayPolicies.shipping_policies?.map(policy => (
                                <option key={policy.id} value={policy.name}>
                                  {policy.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Return Policy */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Return Policy:</label>
                            <select 
                              value={selectedPolicies.ebayReturn} 
                              onChange={(e) => handlePolicyChange('ebayReturn', e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                border: '1px solid var(--color-border)', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                              }}
                            >
                              <option value="">Select a return policy</option>
                              {ebayPolicies.return_policies?.map(policy => (
                                <option key={policy.id} value={policy.name}>
                                  {policy.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Default eBay Category */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Default eBay Category:</label>
                            <select 
                              value={defaultEbayCategory} 
                              onChange={(e) => {
                                setDefaultEbayCategory(e.target.value);
                                setHasUnsavedChanges(true);
                              }}
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                border: '1px solid var(--color-border)', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                              }}
                            >
                              <option value="">Select a default category</option>
                              {ebayCategories && ebayCategories.length > 0 ? ebayCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              )) : (
                                <option disabled>Loading categories...</option>
                              )}
                            </select>
                            <small style={{ color: 'var(--color-text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                              Used when no specific category is set for a product
                            </small>
                          </div>
                        </div>
                      ) : ebayPolicies && !ebayPolicies.success ? (
                        <div style={{ padding: '16px', backgroundColor: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '4px', color: '#856404' }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>eBay Not Connected</p>
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>{ebayPolicies.error}</p>
                          <p style={{ margin: '0', fontSize: '13px' }}>
                            Please connect your eBay account in the <strong>Connections</strong> tab first.
                          </p>
                        </div>
                      ) : (
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>No eBay policies found. Please ensure your eBay account is connected and has business policies set up.</p>
                      )}
                    </div>

                    {/* Etsy Policies Section */}
                    <div className="settings-section" style={{ marginBottom: '32px', padding: '20px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                      <h4 style={{ marginBottom: '16px', color: 'var(--color-text)', fontSize: '18px', fontWeight: '600' }}>Etsy Policies</h4>
                      
                      {isEtsyPoliciesLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="loading-spinner"></span>
                          <span>Loading Etsy profiles...</span>
                        </div>
                      ) : etsyProfiles && etsyProfiles.success ? (
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {/* Shipping Profiles */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Default Shipping Profile:</label>
                            <select 
                              value={selectedPolicies.etsyShipping} 
                              onChange={(e) => handlePolicyChange('etsyShipping', e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                border: '1px solid var(--color-border)', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                              }}
                            >
                              <option value="">Select a shipping profile</option>
                              {etsyProfiles.shipping_profiles?.map(profile => (
                                <option key={profile.id} value={profile.name}>
                                  {profile.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Return Policies */}
                          {etsyProfiles.return_policies && etsyProfiles.return_policies.length > 0 && (
                            <div>
                              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Default Return Policy:</label>
                              <select 
                                value={selectedPolicies.etsyReturn} 
                                onChange={(e) => handlePolicyChange('etsyReturn', e.target.value)}
                                style={{ 
                                  width: '100%', 
                                  padding: '6px 10px', 
                                  border: '1px solid var(--color-border)', 
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  backgroundColor: 'var(--color-surface)',
                                  color: 'var(--color-text)'
                                }}
                              >
                                <option value="">Select a return policy</option>
                                {etsyProfiles.return_policies.map(policy => (
                                  <option key={policy.id} value={policy.name}>
                                    {policy.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      ) : etsyProfiles && !etsyProfiles.success ? (
                        <div style={{ padding: '16px', backgroundColor: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '4px', color: '#856404' }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Etsy Not Connected</p>
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>{etsyProfiles.error}</p>
                          <p style={{ margin: '0', fontSize: '13px' }}>
                            Please connect your Etsy account in the <strong>Connections</strong> tab first.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Default Shipping Profile:</label>
                          <input 
                            type="text"
                            value={selectedPolicies.etsyShipping} 
                            onChange={(e) => setSelectedPolicies(prev => ({ ...prev, etsyShipping: e.target.value }))}
                            placeholder="Enter Etsy shipping profile name"
                            style={{ 
                              width: '100%', 
                              padding: '6px 10px', 
                              border: '1px solid var(--color-border)', 
                              borderRadius: '4px',
                              fontSize: '14px',
                              backgroundColor: 'var(--color-surface)',
                              color: 'var(--color-text)'
                            }}
                          />
                          <small style={{ color: 'var(--color-text-secondary)', marginTop: '4px', display: 'block', fontSize: '12px' }}>
                            Enter the exact name of your Etsy shipping profile as it appears in your Etsy settings.
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Load Policies Buttons */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          fetchEbayPolicies();
                        }}
                        disabled={isEbayPoliciesLoading}
                        className="button-secondary"
                        style={{ flex: 1 }}
                        type="button"
                      >
                        {isEbayPoliciesLoading ? 'Loading...' : 'Reload eBay Policies'}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          fetchEtsyProfiles();
                        }}
                        disabled={isEtsyPoliciesLoading}
                        className="button-secondary"
                        style={{ flex: 1 }}
                        type="button"
                      >
                        {isEtsyPoliciesLoading ? 'Loading...' : 'Reload Etsy Profiles'}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          fetchAllPolicies();
                        }}
                        disabled={isEbayPoliciesLoading || isEtsyPoliciesLoading}
                        className="button-secondary"
                        style={{ flex: 1 }}
                        type="button"
                      >
                        {(isEbayPoliciesLoading || isEtsyPoliciesLoading) ? 'Loading...' : 'Reload Both'}
                      </button>
                    </div>

                  </div>
                )}

                {activeTab === 'data' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Sales Data Management</h3>
                    
                    {/* Reset Sales Data Section */}
                    <div className="settings-section" style={{ marginBottom: '24px', padding: '24px', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'var(--color-surface)' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-text)' }}>Reset Sales Data</h4>
                      <p style={{ margin: '0 0 20px 0', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                        Reset sales statistics by clearing sale dates. Products will remain marked as sold but won't count toward sales statistics.
                      </p>
                      
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleResetSalesData('today')}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #dc2626',
                            background: '#fef2f2',
                            color: '#dc2626',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fecaca';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                          }}
                        >
                          Reset Today's Sales
                        </button>
                        
                        <button
                          onClick={() => handleResetSalesData('monthly')}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #ea580c',
                            background: '#fff7ed',
                            color: '#ea580c',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fed7aa';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fff7ed';
                          }}
                        >
                          Reset This Month's Sales
                        </button>
                        
                        <button
                          onClick={() => handleResetSalesData('total')}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #b91c1c',
                            background: '#fef2f2',
                            color: '#b91c1c',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fecaca';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                          }}
                        >
                          Reset All Sales Data
                        </button>
                      </div>
                      
                      <div style={{ marginTop: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                        <strong>Note:</strong> This action only affects sales statistics. Products will remain marked as sold in your inventory.
                      </div>
                    </div>

                  </div>
                )}
                
                {activeTab === 'account' && (
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>Account Settings</h3>
                    
                    {/* Profile Management Section */}
                    <div className="settings-section" style={{ marginBottom: '24px', padding: '24px', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'var(--color-surface)' }}>
                      <h4 style={{ 
                        margin: '0 0 20px 0', 
                        fontSize: '18px', 
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>🔐</span>
                        Profile & Security
                      </h4>
                      
                      <div style={{ display: 'grid', gap: '16px', maxWidth: '500px' }}>
                        <div style={{
                          padding: '16px',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--border-radius-lg)',
                          background: 'var(--color-surface-secondary)'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: 'var(--color-text-primary)'
                          }}>
                            Change Password
                          </h5>
                          <p style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '13px', 
                            color: 'var(--color-text-secondary)',
                            lineHeight: '1.4'
                          }}>
                            Update your password to keep your account secure.
                          </p>
                          <button
                            onClick={() => showWarning('Password change functionality will be implemented soon. For now, please contact support if you need to change your password.', 'Feature Coming Soon')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              border: '1px solid var(--color-border)',
                              borderRadius: 'var(--border-radius)',
                              background: 'var(--color-surface)',
                              color: 'var(--color-text-primary)',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'var(--color-surface-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'var(--color-surface)';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>🔑</span>
                            Change Password
                          </button>
                        </div>
                        
                        <div style={{
                          padding: '16px',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--border-radius-lg)',
                          background: 'var(--color-surface-secondary)'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: 'var(--color-text-primary)'
                          }}>
                            Update Email Address
                          </h5>
                          <p style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '13px', 
                            color: 'var(--color-text-secondary)',
                            lineHeight: '1.4'
                          }}>
                            Change the email address associated with your account.
                          </p>
                          <button
                            onClick={() => showWarning('Email change functionality will be implemented soon. For now, please contact support if you need to update your email address.', 'Feature Coming Soon')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              border: '1px solid var(--color-border)',
                              borderRadius: 'var(--border-radius)',
                              background: 'var(--color-surface)',
                              color: 'var(--color-text-primary)',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'var(--color-surface-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'var(--color-surface)';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>📧</span>
                            Change Email
                          </button>
                        </div>
                        
                        <div style={{
                          padding: '12px',
                          borderRadius: 'var(--border-radius)',
                          background: '#E0F2FE',
                          border: '1px solid #0284C7'
                        }}>
                          <p style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            color: '#075985',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px'
                          }}>
                            <span style={{ fontSize: '14px' }}>ℹ️</span>
                            <span>
                              <strong>Coming Soon:</strong> Full profile management features including password and email changes will be available in a future update. For immediate assistance, please contact our support team.
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Session Management Section */}
                    <div className="settings-section" style={{ marginBottom: '32px', padding: '24px', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'var(--color-surface)' }}>
                      <h4 style={{ 
                        margin: '0 0 20px 0', 
                        fontSize: '18px', 
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>👤</span>
                        Session Management
                      </h4>
                      
                      <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
                        <div style={{
                          padding: '16px',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--border-radius-lg)',
                          background: 'var(--color-surface-secondary)'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: 'var(--color-text-primary)'
                          }}>
                            Session Management
                          </h5>
                          <p style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '13px', 
                            color: 'var(--color-text-secondary)',
                            lineHeight: '1.4'
                          }}>
                            Log out of your CrossLister account and clear all stored data from this device.
                          </p>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to log out? You will need to log in again to access your account.')) {
                                localStorage.removeItem("authToken");
                                window.location.reload();
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              border: '1px solid #DC2626',
                              borderRadius: 'var(--border-radius)',
                              background: '#DC2626',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#B91C1C';
                              e.target.style.borderColor = '#B91C1C';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#DC2626';
                              e.target.style.borderColor = '#DC2626';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>🚪</span>
                            Logout
                          </button>
                        </div>
                        
                        <div style={{
                          padding: '16px',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--border-radius-lg)',
                          background: 'var(--color-surface-secondary)'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: 'var(--color-text-primary)'
                          }}>
                            Security Information
                          </h5>
                          <div style={{ 
                            display: 'grid',
                            gap: '8px',
                            fontSize: '13px',
                            color: 'var(--color-text-secondary)'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Session Duration:</span>
                              <span style={{ fontWeight: '500' }}>30 days (typical)</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Auto Logout:</span>
                              <span style={{ fontWeight: '500' }}>On browser close</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Data Storage:</span>
                              <span style={{ fontWeight: '500' }}>Local only</span>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{
                          padding: '12px',
                          borderRadius: 'var(--border-radius)',
                          background: '#FEF3C7',
                          border: '1px solid #F59E0B'
                        }}>
                          <p style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            color: '#92400E',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px'
                          }}>
                            <span style={{ fontSize: '14px' }}>⚠️</span>
                            <span>
                              <strong>Note:</strong> Logging out will clear all locally stored authentication data. You'll need to log in again to access your marketplace connections and product data.
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Modal Actions at the bottom */}
        <div style={{ 
          padding: '20px', 
          borderTop: '1px solid var(--color-border)', 
          backgroundColor: 'var(--color-surface)', 
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            className="button-secondary"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="button-primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span
                  className="loading-spinner"
                  style={{ marginRight: "8px" }}
                ></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
      
      {resetConfirmation && (
        <ConfirmationModal
          title={resetConfirmation.title}
          message={resetConfirmation.message}
          onConfirm={confirmResetSalesData}
          onClose={() => setResetConfirmation(null)}
          isDestructive={resetConfirmation.isDestructive}
        />
      )}
    </div>
  );
}

function ToolsModal({ isOpen, onClose, onExport, onPrint, onImport, filteredProductsLength, isPerformingAction }) {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Tools</h2>
          <button onClick={onClose} className="close-button" aria-label="Close">×</button>
        </div>
        
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gap: '12px' }}>
            <button
              onClick={() => { onExport(); onClose(); }}
              className="button-secondary"
              disabled={filteredProductsLength === 0}
              title="Export current view to CSV"
              style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
            >
              Export CSV
            </button>

            <button
              onClick={() => { onPrint(); onClose(); }}
              className="button-secondary"
              disabled={filteredProductsLength === 0}
              title="Print current view"
              style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
            >
              Print
            </button>

            <button
              onClick={() => { onImport(); onClose(); }}
              className="button-secondary"
              title="Import products from CSV"
              style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
            >
              Import CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SyncDropdown({ isOpen, onClose, onWooCommerceSync, onEbaySync, onEtsySync, isPerformingSync, syncingPlatform, triggerRef, showWarning }) {
  const dropdownRef = useRef(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    woocommerce: false,
    ebay: false,
    etsy: false
  });

  // Calculate position relative to trigger button
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && triggerRef?.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      
      let top = triggerRect.bottom + 8;
      let left = triggerRect.left;
      
      // Adjust if dropdown would go off screen
      if (left + dropdownRect.width > window.innerWidth) {
        left = triggerRect.right - dropdownRect.width;
      }
      
      if (top + dropdownRect.height > window.innerHeight) {
        top = triggerRect.top - dropdownRect.height - 8;
      }
      
      setPosition({ top, left });
    }
  }, [isOpen, triggerRef]);

  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const handleSyncSelected = async () => {
    const selected = Object.entries(selectedPlatforms)
      .filter(([platform, isSelected]) => isSelected)
      .map(([platform]) => platform);
    
    if (selected.length === 0) {
      showWarning('Please select at least one platform to sync', 'No Platform Selected');
      return;
    }

    // Close dropdown first
    onClose();

    // Sync selected platforms sequentially
    for (const platform of selected) {
      switch (platform) {
        case 'woocommerce':
          await onWooCommerceSync();
          break;
        case 'ebay':
          await onEbaySync();
          break;
        case 'etsy':
          await onEtsySync();
          break;
      }
    }

    // Reset selections
    setSelectedPlatforms({
      woocommerce: false,
      ebay: false,
      etsy: false
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop to close dropdown */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 999,
        }}
      />
      
      {/* Dropdown content */}
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 1000,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          minWidth: '200px',
          padding: '8px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* Header */}
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--color-text-primary)',
            marginBottom: '2px'
          }}>
            Select Platforms to Sync
          </div>
          
          {/* Platform checkboxes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {[
              { key: 'woocommerce', name: 'WooCommerce', title: 'Sync products from WooCommerce store (active, in-stock listings with SKUs only)' },
              { key: 'ebay', name: 'eBay', title: 'Sync active listings from eBay store (listings with SKUs only)' },
              { key: 'etsy', name: 'Etsy', title: 'Sync active listings from Etsy shop (listings with SKUs only)' }
            ].map(platform => (
              <label
                key={platform.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: isPerformingSync ? 'default' : 'pointer',
                  padding: '4px 6px',
                  borderRadius: 'var(--border-radius)',
                  opacity: isPerformingSync ? 0.6 : 1,
                  transition: 'background-color 0.15s ease',
                }}
                title={platform.title}
                onMouseEnter={(e) => {
                  if (!isPerformingSync) {
                    e.target.style.backgroundColor = 'var(--color-surface-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPlatforms[platform.key]}
                  onChange={() => handlePlatformToggle(platform.key)}
                  disabled={isPerformingSync}
                  style={{
                    marginRight: '8px',
                    accentColor: 'var(--color-primary)',
                    cursor: 'inherit'
                  }}
                />
                <span style={{
                  fontSize: '13px',
                  color: 'var(--color-text-primary)',
                  fontWeight: isPerformingSync && syncingPlatform === platform.key ? '600' : '500'
                }}>
                  {isPerformingSync && syncingPlatform === platform.key ? `Syncing ${platform.name}...` : platform.name}
                </span>
              </label>
            ))}
          </div>
          
          {/* Action buttons */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid var(--color-border-light)'
          }}>
            <button
              onClick={handleSyncSelected}
              disabled={isPerformingSync || Object.values(selectedPlatforms).every(v => !v)}
              style={{
                flex: 1,
                padding: '6px 10px',
                border: 'none',
                borderRadius: 'var(--border-radius)',
                background: 'var(--color-primary)',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: isPerformingSync || Object.values(selectedPlatforms).every(v => !v) ? 'default' : 'pointer',
                opacity: isPerformingSync || Object.values(selectedPlatforms).every(v => !v) ? 0.5 : 1,
                transition: 'opacity 0.15s ease'
              }}
            >
              Sync Selected
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--border-radius)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--color-surface-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              Cancel
            </button>
          </div>
          
          {/* Info text */}
          <div style={{ 
            fontSize: '11px', 
            color: 'var(--color-text-tertiary)', 
            padding: '4px 0',
            lineHeight: '1.3'
          }}>
            Only imports active products/listings with SKUs from your connected marketplaces.
          </div>
        </div>
      </div>
    </>
  );
}

// Category Picker Modal Component
function CategoryPickerModal({ categories, selectedCategoryId, onSelectCategory, onClose }) {
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Organize categories by hierarchy
  const categoryTree = React.useMemo(() => {
    if (!categories || categories.length === 0) return {};
    
    const tree = {};
    const categoryMap = {};
    
    // First, create a map of all categories
    categories.forEach(cat => {
      categoryMap[cat.id] = cat;
    });
    
    // Build the tree structure
    categories.forEach(cat => {
      if (!cat.parent_id || cat.level === 1) {
        // Top level category
        tree[cat.id] = {
          ...cat,
          children: []
        };
      }
    });
    
    // Add children to parents
    categories.forEach(cat => {
      if (cat.parent_id && tree[cat.parent_id]) {
        tree[cat.parent_id].children.push(cat);
      } else if (cat.parent_id) {
        // Find grandparent
        const parent = categoryMap[cat.parent_id];
        if (parent && parent.parent_id && tree[parent.parent_id]) {
          if (!tree[parent.parent_id].children.find(c => c.id === parent.id)) {
            tree[parent.parent_id].children.push({
              ...parent,
              children: []
            });
          }
          const parentInTree = tree[parent.parent_id].children.find(c => c.id === parent.id);
          if (parentInTree && !parentInTree.children) {
            parentInTree.children = [];
          }
          if (parentInTree) {
            parentInTree.children.push(cat);
          }
        }
      }
    });
    
    return tree;
  }, [categories]);
  
  // Filter categories based on search
  const filteredCategories = React.useMemo(() => {
    if (!searchTerm) return categoryTree;
    
    const filtered = {};
    const term = searchTerm.toLowerCase();
    
    Object.values(categoryTree).forEach(parent => {
      if (parent.name && parent.name.toLowerCase().includes(term)) {
        filtered[parent.id] = parent;
      } else {
        // Check children
        const matchingChildren = parent.children.filter(child => 
          (child.name && child.name.toLowerCase().includes(term)) ||
          (child.children && child.children.some(gc => gc.name && gc.name.toLowerCase().includes(term)))
        );
        if (matchingChildren.length > 0) {
          filtered[parent.id] = {
            ...parent,
            children: matchingChildren
          };
        }
      }
    });
    
    return filtered;
  }, [categoryTree, searchTerm]);
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', height: '600px' }}>
        <div className="modal-header">
          <h2>Select eBay Category</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div style={{ padding: '20px' }}>
          {/* Search Bar */}
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          
          {/* Category Selection Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', height: '400px' }}>
            {/* Parent Categories */}
            <div style={{ 
              border: '1px solid var(--color-border)', 
              borderRadius: '6px',
              overflow: 'auto',
              padding: '10px'
            }}>
              <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>Main Categories</h4>
              {Object.values(filteredCategories).map(parent => (
                <div
                  key={parent.id}
                  onClick={() => {
                    setSelectedParent(parent);
                    setSelectedChild(null);
                  }}
                  style={{
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedParent?.id === parent.id ? '#e3f2fd' : 'transparent',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    fontSize: '13px'
                  }}
                >
                  {parent.name.replace(/^[└─├│\s]+/, '')}
                </div>
              ))}
            </div>
            
            {/* Child Categories */}
            <div style={{ 
              border: '1px solid var(--color-border)', 
              borderRadius: '6px',
              overflow: 'auto',
              padding: '10px'
            }}>
              <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>Subcategories</h4>
              {selectedParent && selectedParent.children && selectedParent.children.map(child => (
                <div
                  key={child.id}
                  onClick={() => {
                    if (child.leaf) {
                      onSelectCategory(child.id);
                    } else {
                      setSelectedChild(child);
                    }
                  }}
                  style={{
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedChild?.id === child.id ? '#e3f2fd' : 
                                   (child.leaf && selectedCategoryId === child.id ? '#c8e6c9' : 'transparent'),
                    borderRadius: '4px',
                    marginBottom: '4px',
                    fontSize: '13px',
                    fontWeight: child.leaf ? '500' : 'normal'
                  }}
                >
                  {child.name} {child.leaf && '✓'}
                </div>
              ))}
            </div>
            
            {/* Grandchild Categories */}
            <div style={{ 
              border: '1px solid var(--color-border)', 
              borderRadius: '6px',
              overflow: 'auto',
              padding: '10px'
            }}>
              <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>Specific Categories</h4>
              {selectedChild && selectedChild.children && selectedChild.children.map(grandchild => (
                <div
                  key={grandchild.id}
                  onClick={() => onSelectCategory(grandchild.id)}
                  style={{
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedCategoryId === grandchild.id ? '#c8e6c9' : 'transparent',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {grandchild.name} ✓
                </div>
              ))}
            </div>
          </div>
          
          {/* Current Selection */}
          <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
            <strong>Selected: </strong>
            {selectedCategoryId ? 
              categories.find(c => c.id === selectedCategoryId)?.name || selectedCategoryId 
              : 'None'}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductDetailModal({ product, onClose, onSave, showSuccess, showError, showWarning, platformSettings }) {
  const [formData, setFormData] = useState({
    name: product.name,
    sku: product.sku,
    price: product.price,
    description: product.description || "",
    listings: product.listings || {},
    ebay_category: product.ebay_category || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [crossListingState, setCrossListingState] = useState({});
  const [ebayCategories, setEbayCategories] = useState([]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  const platforms = [
    { key: "woocommerce", name: "WooCommerce", initial: "W", color: "var(--color-primary)" },
    { key: "ebay", name: "eBay", initial: "E", color: "var(--color-primary)" },
    { key: "etsy", name: "Etsy", initial: "Et", color: "var(--color-primary)" },
    { key: "vinted", name: "Vinted", initial: "V", color: "var(--color-primary)" },
  ].filter(platform => platformSettings?.[platform.key]?.enabled !== false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleListingChange = (platform, field, value) => {
    setFormData((prev) => ({
      ...prev,
      listings: {
        ...prev.listings,
        [platform]: {
          ...prev.listings[platform],
          [field]: value,
        },
      },
    }));
  };

  const removeListing = (platform) => {
    setFormData((prev) => ({
      ...prev,
      listings: {
        ...prev.listings,
        [platform]: undefined,
      },
    }));
  };

  const handleCrossListToPlatform = async (platformKey) => {
    const platform = platforms.find(p => p.key === platformKey);
    if (!platform) return;
    
    try {
      setCrossListingState(prev => ({ ...prev, [platformKey]: 'loading' }));
      
      let response;
      if (platformKey === 'vinted') {
        // Vinted uses extension directly - no API needed
        console.log('🔍 Checking for Vinted extension...', window.CrossListerExtension);
        
        if (window.CrossListerExtension && window.CrossListerExtension.isInstalled) {
          console.log('✅ Extension detected! Calling crosslistToVinted...');
          try {
            // Get pricing rules from API
            let vintedMarkup = 0;
            let roundTo99 = false;
            try {
              const credsResponse = await apiClient.get('/api/credentials/');
              vintedMarkup = credsResponse.data?.pricing_rules?.vinted_markup || 0;
              roundTo99 = credsResponse.data?.pricing_rules?.round_to_99 || false;
            } catch (error) {
              console.warn('Could not fetch pricing rules, using defaults:', error);
            }
            let vintedPrice = parseFloat(product.price) * (1 + vintedMarkup / 100);
            
            // Round to .99 if enabled
            if (roundTo99) {
              vintedPrice = Math.floor(vintedPrice) + 0.99;
            }
            
            // Debug product images before sending to extension
            console.log('🖼️ DEBUG: product.images:', product.images);
            console.log('🖼️ DEBUG: product.images type:', typeof product.images);
            console.log('🖼️ DEBUG: product.images length:', product.images?.length);
            console.log('🖼️ DEBUG: Full product object:', product);
            
            const imagesToSend = product.images ? product.images.map(img => img.src) : [];
            console.log('🖼️ DEBUG: imagesToSend:', imagesToSend);
            
            // Use extension directly with product data
            await window.CrossListerExtension.crosslistToVinted({
              title: product.name,
              name: product.name,
              description: product.description,
              price: vintedPrice.toFixed(2),
              images: imagesToSend,
              sku: product.sku
            });
            
            showSuccess('🚀 Extension will handle everything automatically!\\n\\n✨ The Vinted page will open and form will be filled\\n📸 Images will be uploaded\\n🎯 You just need to select category & condition\\n\\nProduct: ' + product.name + '\\nPrice: £' + vintedPrice.toFixed(2) + (vintedMarkup > 0 ? ' (original: £' + product.price + ', +' + vintedMarkup + '%)' : ''), 'Extension Active');
            
          } catch (error) {
            console.error('Extension error:', error);
            
            // Check if it's a context invalidation error
            if (error.message && (error.message.includes('context invalidated') || error.message.includes('Extension context') || error.message.includes('refresh'))) {
              showError('🔄 Extension Needs Refresh\\n\\nThe Chrome extension was recently updated or reloaded.\\n\\n✅ Solution: Refresh this page (F5) and try again.\\n\\nThis is a one-time issue after extension updates.', 'Extension Context Issue');
              return;
            }
            
            showError('❌ Extension Error\\n\\nThe Vinted extension encountered an error.\\n\\nPlease make sure:\\n✅ Extension is properly installed\\n✅ Pop-ups are allowed\\n✅ Try refreshing the page', 'Extension Error');
          }
        } else {
          // Extension not installed
          showError('⚠️ CrossLister Extension Required\\n\\nFor Vinted crosslisting, please install the CrossLister Chrome extension.\\n\\nThe extension will:\\n✅ Fill forms automatically\\n✅ Upload images seamlessly\\n✅ Save you tons of time!', 'Extension Required');
        }
      } else if (platformKey === 'etsy') {
        // Etsy uses extension directly - no API needed
        console.log('🔍 Checking for Etsy extension...', window.CrossListerExtension);
        
        if (window.CrossListerExtension && window.CrossListerExtension.isInstalled) {
          console.log('✅ Extension detected! Calling crosslistToEtsy...');
          try {
            // Use extension directly with product data
            await window.CrossListerExtension.crosslistToEtsy({
              title: product.name,
              name: product.name,
              description: product.description,
              price: product.price,
              images: product.images ? product.images.map(img => img.src) : [],
              sku: product.sku
            });
            
            showSuccess('🚀 Extension will handle everything automatically!\\n\\n✨ The Etsy page will open and form will be filled\\n📸 Images will be uploaded\\n🎯 Complete the listing on Etsy\\n\\nProduct: ' + product.name + '\\nPrice: £' + product.price, 'Extension Active');
            
          } catch (error) {
            console.error('Extension error:', error);
            
            // Check if it's a context invalidation error
            if (error.message && (error.message.includes('context invalidated') || error.message.includes('Extension context') || error.message.includes('refresh'))) {
              showError('🔄 Extension Needs Refresh\\n\\nThe Chrome extension was recently updated or reloaded.\\n\\n✅ Solution: Refresh this page (F5) and try again.\\n\\nThis is a one-time issue after extension updates.', 'Extension Context Issue');
              return;
            }
            
            showError('❌ Extension Error\\n\\nThe Etsy extension encountered an error.\\n\\nPlease make sure:\\n✅ Extension is properly installed\\n✅ Pop-ups are allowed\\n✅ Try refreshing the page', 'Extension Error');
          }
        } else {
          // Extension not installed
          showError('⚠️ CrossLister Extension Required\\n\\nFor Etsy crosslisting, please install the CrossLister Chrome extension.\\n\\nThe extension will:\\n✅ Fill forms automatically\\n✅ Upload images seamlessly\\n✅ Save you tons of time!', 'Extension Required');
        }
      } else {
        // eBay uses direct API calls
        response = await apiClient.post(`/api/crosslist/${platformKey}/`, {
          product_id: product.id
        });
        
        if (response.data.success) {
          // Update formData with new listing info
          setFormData(prev => ({
            ...prev,
            listings: {
              ...prev.listings,
              [platformKey]: {
                id: response.data.item_id || response.data.listing_id,
                url: response.data.listing_url,
                price: response.data.price || product.price,
                status: platformKey === 'ebay' ? 'scheduled' : 'draft'
              }
            }
          }));
          
          let message = response.data.message;
          if (platformKey === 'ebay' && response.data.scheduled_time) {
            const scheduleDate = new Date(response.data.scheduled_time).toLocaleDateString();
            message += `\n\nScheduled to go live: ${scheduleDate}`;
          }
          if (platformKey === 'etsy' && response.data.images_uploaded) {
            message += `\nImages uploaded: ${response.data.images_uploaded}`;
          }
          
          showSuccess(message, 'Crosslist Success');
        }
      }
      
      setCrossListingState(prev => ({ ...prev, [platformKey]: 'success' }));
      
    } catch (error) {
      console.error(`Cross-listing to ${platform.name} failed:`, error);
      setCrossListingState(prev => ({ ...prev, [platformKey]: 'error' }));
      
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      showError(`Failed to cross-list to ${platform.name}:\n${errorMessage}`, 'Crosslist Failed');
    }
  };

  // Fetch eBay categories on component mount
  useEffect(() => {
    const fetchEbayCategories = async () => {
      try {
        const response = await apiClient.get('/api/ebay-categories/');
        console.log('ProductModal eBay categories response:', response.data);
        
        if (response.data.success) {
          const categories = response.data.categories || [];
          console.log(`ProductModal loaded ${categories.length} eBay categories`);
          setEbayCategories(categories);
        } else {
          console.error('ProductModal eBay categories API error:', response.data.error);
          setEbayCategories([]);
        }
      } catch (error) {
        console.error('ProductModal failed to fetch eBay categories:', error);
        setEbayCategories([]); // Set empty array on error
      }
    };
    
    fetchEbayCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(product.id, formData);
      onClose();
    } catch (error) {
      showError("Failed to save product: " + error.message, 'Save Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const images = product.images || [];

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-content"
        style={{ maxWidth: "90vw", width: "1100px", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="modal-header">
          <h2>Product Details</h2>
          <button onClick={onClose} className="close-button">
            ×
          </button>
        </div>

        <div style={{ display: "flex", gap: "24px" }}>
          {/* Left Column - Images and Platforms */}
          <div style={{ flex: "0 0 500px" }}>
            {/* Images Section - Smaller */}
            <h3 style={{ marginTop: "0", marginBottom: "12px", fontSize: "16px" }}>
              Product Images
            </h3>
              {images.length > 0 ? (
                <div>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
                    {/* Previous Button */}
                    {images.length > 1 && (
                      <button
                        onClick={() => setActiveImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}
                        style={{
                          background: "var(--color-surface-secondary)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        ←
                      </button>
                    )}
                    
                    {/* Main Image - Much smaller */}
                    <img
                      src={
                        images[activeImageIndex]?.src ||
                        "https://placehold.co/250x250?text=No+Image"
                      }
                      alt={product.name}
                      style={{
                        width: "250px",
                        height: "250px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid var(--color-border)",
                        boxShadow: "var(--shadow-sm)"
                      }}
                    />
                    
                    {/* Next Button */}
                    {images.length > 1 && (
                      <button
                        onClick={() => setActiveImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1)}
                        style={{
                          background: "var(--color-surface-secondary)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        →
                      </button>
                    )}
                  </div>
                
                  {/* Image Counter and Thumbnails */}
                  {images.length > 1 && (
                    <>
                      <div style={{ textAlign: "center", marginBottom: "8px", fontSize: "11px", color: "var(--color-text-secondary)" }}>
                        {activeImageIndex + 1} of {images.length}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                          overflowX: "auto",
                          maxWidth: "100%",
                          padding: "4px 0",
                          marginBottom: "16px"
                        }}
                      >
                        {images.map((image, index) => (
                          <img
                            key={`image-${index}`}
                            src={image.src}
                            alt={`${product.name} ${index + 1}`}
                            onClick={() => setActiveImageIndex(index)}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              border:
                                activeImageIndex === index
                                  ? "2px solid var(--color-primary)"
                                  : "1px solid var(--color-border)",
                              cursor: "pointer",
                              transition: "all 0.2s ease"
                            }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    width: "250px",
                    height: "250px",
                    border: "1px dashed var(--color-border)",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--color-text-secondary)",
                    margin: "0 auto 20px",
                    backgroundColor: "var(--color-surface-secondary)",
                    fontSize: "14px"
                  }}
                >
                  No images available
                </div>
              )}
            
            {/* Platform Listings - Same column as images */}
            <h3 style={{ marginTop: "20px", marginBottom: "12px", fontSize: "16px" }}>
              Platform Listings
            </h3>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
              {platforms.map((platform) => {
                const listing = formData.listings[platform.key];
                const isListed = listing && listing.url;

                return (
                  <div
                    key={platform.key}
                    style={{
                      padding: "10px",
                      border: `1px solid ${
                        isListed ? platform.color : "#E5E7EB"
                      }`,
                      borderRadius: "6px",
                      background: isListed ? `${platform.color}08` : "#FAFBFC",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                      }}
                    >
                      <div 
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '4px', 
                          backgroundColor: platform.color, 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: '10px', 
                          fontWeight: '600' 
                        }}
                      >
                        {platform.initial}
                      </div>
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>{platform.name}</span>
                      {isListed && (
                        <span
                          style={{
                            background: platform.color,
                            color: "white",
                            padding: "1px 6px",
                            borderRadius: "8px",
                            fontSize: "10px",
                            fontWeight: "600",
                          }}
                        >
                          LISTED
                        </span>
                      )}
                    </div>

                    <input
                      type="url"
                      placeholder={`${platform.name} listing URL`}
                      value={listing?.url || ""}
                      onChange={(e) =>
                        handleListingChange(platform.key, "url", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        border: "1px solid #D1D5DB",
                        borderRadius: "6px",
                        fontSize: "13px",
                        backgroundColor: "var(--color-surface)",
                        color: "var(--color-text)"
                      }}
                    />

                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        gap: "8px",
                      }}
                    >
                      {isListed ? (
                        <>
                          <button
                            type="button"
                            onClick={() => window.open(listing.url, "_blank")}
                            style={{
                              padding: "4px 8px",
                              background: platform.color,
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            View Listing
                          </button>
                          <button
                            type="button"
                            onClick={() => removeListing(platform.key)}
                            style={{
                              padding: "4px 8px",
                              background: "#EF4444",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleCrossListToPlatform(platform.key)}
                          disabled={crossListingState[platform.key] === 'loading'}
                          style={{
                            padding: "6px 12px",
                            background: crossListingState[platform.key] === 'loading' 
                              ? "#9CA3AF" 
                              : platform.color,
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: crossListingState[platform.key] === 'loading' 
                              ? "not-allowed" 
                              : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {crossListingState[platform.key] === 'loading' ? (
                            <>
                              <span style={{
                                width: "12px",
                                height: "12px",
                                border: "2px solid transparent",
                                borderTop: "2px solid white",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite"
                              }}></span>
                              Listing...
                            </>
                          ) : (
                            <>
                              List on {platform.name}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Product Information */}
          <div style={{ flex: "1", minWidth: "0" }}>
            <h3 style={{ marginTop: "0", marginBottom: "16px", fontSize: "16px" }}>
              Product Information
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Product Name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="sku">SKU</label>
                <input
                  id="sku"
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="price">Price (£)</label>
                <input
                  id="price"
                  type="number"
                  name="price"
                  step="0.01"
                  min="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ebay_category">eBay Category (Optional)</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    id="ebay_category_display"
                    value={formData.ebay_category ? 
                      ebayCategories.find(cat => cat.id === formData.ebay_category)?.name || formData.ebay_category 
                      : ''}
                    placeholder="Click to select a category..."
                    readOnly
                    onClick={() => setShowCategoryPicker(true)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      backgroundColor: 'white'
                    }}
                  />
                  {formData.ebay_category && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({...formData, ebay_category: ''});
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid var(--color-border)',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <small style={{ color: 'var(--color-text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Leave blank to use default category from settings or automatic detection
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  rows="8"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Product description..."
                />
              </div>

              <div className="modal-actions" style={{ marginTop: "24px" }}>
                <button
                  type="button"
                  onClick={onClose}
                  className="button-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Category Picker Modal */}
      {showCategoryPicker && (
        <CategoryPickerModal
          categories={ebayCategories}
          selectedCategoryId={formData.ebay_category}
          onSelectCategory={(categoryId) => {
            setFormData({...formData, ebay_category: categoryId});
            setShowCategoryPicker(false);
          }}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}
    </div>
  );
}

function Navbar({ activeTab, setActiveTab, productCounts = {} }) {
  const tabs = [
    { key: "active", label: "Products", count: productCounts.active },
    { key: "sold", label: "Sales", count: productCounts.sold },
    { key: "inactive", label: "Inactive", count: productCounts.inactive },
    {
      key: "action_required",
      label: "Action Required",
      count: productCounts.action_required,
    },
  ];

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          className={`nav-tab ${activeTab === tab.key ? "active" : ""}`}
          onClick={() => {
            console.log(`DEBUG: Switching to tab: ${tab.key}`);
            setActiveTab(tab.key);
          }}
          aria-pressed={activeTab === tab.key}
        >
          <span className="tab-label">{tab.label}</span>
          {typeof tab.count === "number" && (
            <span className="tab-count">{tab.count}</span>
          )}
        </button>
      ))}
    </nav>
  );
}

const ChannelIcon = React.memo(({ platform, active, listingUrl }) => {
  const platformConfig = {
    Vinted: { initial: "V" },
    eBay: { initial: "E" },
    Etsy: { initial: "Et" },
    WooCommerce: { initial: "W" },
  };

  const config = platformConfig[platform] || {
    initial: "?",
  };

  const handleClick = (e) => {
    if (active && listingUrl) {
      e.stopPropagation();
      window.open(listingUrl, "_blank");
    }
  };

  return (
    <div
      className={`channel-icon ${active ? "active-platform" : "inactive"} ${
        active && listingUrl ? "clickable" : ""
      }`}
      title={`${platform}${
        active ? " (Click to view listing)" : " (Not listed)"
      }`}
      onClick={handleClick}
      role={active && listingUrl ? "button" : undefined}
      tabIndex={active && listingUrl ? 0 : undefined}
    >
      {config.initial}
    </div>
  );
});

const ProductRow = React.memo(function ProductRow({
  product,
  isSelected,
  onSelectRow,
  visibleColumns,
  onViewProduct,
  onCrossList,
  onDelist,
  onMarkAsSold,
  onMarkAsActive,
  onDelete,
  onToggleActionsMenu,
  activeTab,
  platformSettings,
}) {
  const buttonRef = React.useRef(null);
  
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const position = {
        top: rect.bottom + 4,
        left: rect.right - 140 // 140px is min-width of menu
      };
      onToggleActionsMenu(product, position);
    }
  };
  
  // Safety check for product
  if (!product || typeof product !== 'object') {
    return null;
  }
  
  const handleRowClick = (e) => {
    if (e.target.tagName === "BUTTON" || e.target.tagName === "INPUT") return;
    onViewProduct(product);
  };

  const getOverallStatus = () => {
    if (product.status === "sold") return { status: "Sold", color: "#EF4444" };
    if (product.status === "action_required")
      return { status: "Inactive", color: "#F59E0B" };

    const listings = product.listings || {};
    const hasListings = Object.values(listings).some(
      (listing) => listing && listing.url
    );

    return hasListings
      ? { status: "Active", color: "#10B981" }
      : { status: "Inactive", color: "#6B7280" };
  };

  // Remove unused statusInfo

  return (
    <tr
      className={`product-row ${isSelected ? "selected" : ""}`}
      onClick={handleRowClick}
      style={{ cursor: "pointer" }}
    >
      <td 
        onClick={(e) => {
          e.stopPropagation();
          onSelectRow(product.id);
        }}
        style={{ 
          cursor: 'pointer',
          padding: '12px',
          textAlign: 'center'
        }}
        title={`Click to ${isSelected ? 'deselect' : 'select'} ${product.name}`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelectRow(product.id)}
          className="checkbox"
          aria-label={`Select ${product.name}`}
          style={{ pointerEvents: 'none' }}
        />
      </td>
      <td>
        <div className="product-info">
          <img
            src={product.images?.[0]?.src || "https://placehold.co/56x56/f9fafb/e5e7eb?text=N/A"}
            alt={product.name}
            className="product-image"
          />
          <div className="product-name">{product.name}</div>
        </div>
      </td>
      <td>
        {product.sku}
      </td>
      <td>
        {product.price ? `£${parseFloat(product.price).toFixed(2)}` : 'N/A'}
      </td>
      <td>
        <span className={`status-pill ${product.status?.replace("_", "-")}`}>
          {product.status?.replace("_", " ").toUpperCase()}
        </span>
      </td>
      <td>
        <div className="channel-icons-container">
          {(['woocommerce', 'ebay', 'etsy', 'vinted']).filter(platform => platformSettings[platform]?.enabled !== false).map(platform => {
            const listing = product.listings?.[platform];
            const isActive = listing && listing.url;
            const initial = platform === 'woocommerce' ? 'W' : platform === 'ebay' ? 'E' : platform === 'etsy' ? 'Et' : 'V';
            return (
              <div
                key={platform}
                className={`channel-icon ${isActive ? 'active-platform' : 'inactive'}`}
                title={platform}
              >
                {initial}
              </div>
            );
          })}
        </div>
      </td>

      {/* Action Required Column (only for action_required tab) */}
      {activeTab === 'action_required' && (
        <td className="action-info-column" style={{ padding: '12px 8px' }}>
          {product.action_info && (
            <div>
              <div className={`action-info-type ${product.action_info.type === 'manual_delist_required' ? 'manual-delist' : 'delisting-failed'}`}>
                {product.action_info.type === 'manual_delist_required' ? 'Manual Delist' : 'Failed Delist'}
              </div>
              <div className="action-info-message">
                {product.action_info.message}
              </div>
              {product.action_info.listing_url && (
                <div style={{ marginTop: '4px' }}>
                  <a 
                    href={product.action_info.listing_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: 'var(--color-primary)', 
                      textDecoration: 'none',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    → Open Listing
                  </a>
                </div>
              )}
            </div>
          )}
        </td>
      )}

      <td onClick={(e) => e.stopPropagation()} style={{ overflow: 'visible', position: 'relative' }}>
        <button
          ref={buttonRef}
          onClick={handleMenuToggle}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            color: 'var(--color-text-secondary)'
          }}
          title="Product actions"
        >
          ⋮
        </button>
      </td>
      {visibleColumns.date_added && <td>{formatDate(product.date_added)}</td>}
      {visibleColumns.last_updated && (
        <td>{formatDate(product.last_updated)}</td>
      )}
      {visibleColumns.date_sold && <td>{formatDate(product.date_sold)}</td>}
      {visibleColumns.description && (
        <td className="description-cell" title={stripHtmlTags(product.description)}>
          {truncateText(product.description)}
        </td>
      )}
      {visibleColumns.product_id && <td>{product.id}</td>}
    </tr>
  );
});

function ToolsDropdown({ isOpen, onClose, onExport, onPrint, onImport, onSaleDetection, triggerRef, filteredProductsLength, isPerformingAction, isRunningSaleDetection }) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && triggerRef?.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      
      let top = triggerRect.bottom + 8;
      let left = triggerRect.left;
      
      // Adjust if dropdown would go off screen
      if (left + dropdownRect.width > window.innerWidth) {
        left = triggerRect.right - dropdownRect.width;
      }
      
      if (top + dropdownRect.height > window.innerHeight) {
        top = triggerRect.top - dropdownRect.height - 8;
      }
      
      setPosition({ top, left });
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop to close dropdown */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 999,
        }}
      />
      
      {/* Dropdown content */}
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 1000,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          minWidth: '160px',
          padding: '6px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <button
            onClick={() => {
              onExport();
              onClose();
            }}
            disabled={isPerformingAction}
            className="dropdown-menu-item"
          >
            Export CSV ({filteredProductsLength} products)
          </button>
          
          <button
            onClick={() => {
              onPrint();
              onClose();
            }}
            disabled={isPerformingAction}
            className="dropdown-menu-item"
          >
            Print List
          </button>
          
          <button
            onClick={() => {
              onImport();
              onClose();
            }}
            disabled={isPerformingAction}
            className="dropdown-menu-item"
          >
            Import CSV
          </button>
          
          <div style={{ height: '1px', background: 'var(--color-border)', margin: '4px 0' }} />
          
          <button
            onClick={() => {
              onSaleDetection();
              onClose();
            }}
            disabled={isPerformingAction || isRunningSaleDetection}
            className="dropdown-menu-item"
          >
            {isRunningSaleDetection ? 'Detecting Sales...' : 'Detect Sales'}
          </button>
        </div>
      </div>
    </>
  );
}

// Global Dropdown Menu Component
const GlobalDropdownMenu = ({ isVisible, position, onClose, product, activeTab, onCrossList, onMarkAsSold, onDelete, onDelist, onMarkAsActive }) => {
  // Add click outside handler
  React.useEffect(() => {
    if (!isVisible) return;
    
    const handleClickOutside = (e) => {
      // Check if click is on the dropdown or its children
      const dropdownElement = document.querySelector('.dropdown-menu');
      if (dropdownElement && !dropdownElement.contains(e.target)) {
        onClose();
      }
    };
    
    // Use capture phase to ensure we get the event before other handlers
    document.addEventListener('click', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isVisible, onClose]);
  
  if (!isVisible) return null;
  
  return (
      <div 
        className="dropdown-menu"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`
        }}
      >
        {activeTab === 'active' && (
          <>
            <button
              className="dropdown-menu-item"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCrossList(product);
                onClose();
              }}
            >
              Crosslist
            </button>
            <button
              className="dropdown-menu-item success"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onMarkAsSold(product);
                onClose();
              }}
            >
              Mark as Sold
            </button>
            <button
              className="dropdown-menu-item danger"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(product);
                onClose();
              }}
            >
              Delete
            </button>
            <button
              className="dropdown-menu-item"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelist(product);
                onClose();
              }}
            >
              Delist
            </button>
          </>
        )}
        {activeTab === 'sold' && (
          <>
            <button
              className="dropdown-menu-item primary"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onMarkAsActive(product);
                onClose();
              }}
            >
              Mark as Active
            </button>
            <button
              className="dropdown-menu-item danger"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(product);
                onClose();
              }}
            >
              Delete
            </button>
          </>
        )}
        {activeTab === 'inactive' && (
          <>
            <button
              className="dropdown-menu-item primary"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onMarkAsActive(product);
                onClose();
              }}
            >
              Mark as Active
            </button>
            <button
              className="dropdown-menu-item success"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCrossList(product);
                onClose();
              }}
            >
              Crosslist
            </button>
            <button
              className="dropdown-menu-item danger"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(product);
                onClose();
              }}
            >
              Delete
            </button>
          </>
        )}
        {activeTab === 'action_required' && (
          <>
            <button
              className="dropdown-menu-item success"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onMarkAsSold(product);
                onClose();
              }}
            >
              Mark as Sold
            </button>
            <button
              className="dropdown-menu-item primary"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onMarkAsActive(product);
                onClose();
              }}
            >
              Mark as Active
            </button>
            <button
              className="dropdown-menu-item danger"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(product);
                onClose();
              }}
            >
              Delete
            </button>
          </>
        )}
      </div>
  );
};

function ConfirmationModal({
  title,
  message,
  confirmText,
  onConfirm,
  onClose,
  isDestructive = false,
}) {
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>{title}</h2>
          <button onClick={onClose} className="close-button" aria-label="Close">
            ×
          </button>
        </div>
        <div style={{ 
          whiteSpace: 'pre-line', 
          lineHeight: '1.5',
          marginBottom: '24px',
          fontSize: '14px'
        }}>
          {message}
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="button-secondary">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={isDestructive ? "button-danger" : "button-primary"}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Function to get the most recent listing date from source platforms
const getSourceCreationDate = (product) => {
  const listings = product.listings || {};
  const dates = [];

  // Check WooCommerce creation date
  if (listings.woocommerce?.date_created) {
    dates.push(new Date(listings.woocommerce.date_created));
  }
  
  // Check eBay creation date
  if (listings.ebay?.start_time) {
    dates.push(new Date(listings.ebay.start_time));
  }
  
  // Check Etsy creation date
  if (listings.etsy?.created_timestamp) {
    dates.push(new Date(listings.etsy.created_timestamp * 1000)); // Etsy uses Unix timestamp
  }
  
  // Check Vinted creation date
  if (listings.vinted?.created_at) {
    dates.push(new Date(listings.vinted.created_at));
  }

  // Return the most recent date for "newest first" sorting, or null if no dates found
  return dates.length > 0 ? new Date(Math.max(...dates)) : null;
};

function ProductDashboard({ products, allProducts, onProductsChange, onEditProduct, onCrossList, onDelist, onMarkAsSold, onMarkAsActive, onDelete, onToggleActionsMenu, setCrosslistModal, activeTab = "products", searchTerm, setSearchTerm, debouncedSearchTerm, isPerformingAction, setIsPerformingAction, showError, showWarning, showSuccess, platformSettings }) {
  const [sortBy, setSortBy] = useState("date_desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    date_added: true,
    last_updated: false,
    date_sold: activeTab === 'sold',
    description: false,
    product_id: false,
  });
  const [showDisplayMenu, setShowDisplayMenu] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    priceMin: '',
    priceMax: '',
    dateFrom: '',
    dateTo: '',
    platforms: [],
    notOnPlatforms: []
  });
  
  // Track if user is actively searching to prevent focus loss
  const [isUserTyping, setIsUserTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Restore focus if user was typing and component re-rendered
  useEffect(() => {
    if (isUserTyping && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [products, isUserTyping]);
  


  // Filtered and sorted products - must be defined before shortcuts that use it
  const filteredAndSortedProducts = useMemo(() => {
    const productArray = Array.isArray(products) ? products : [];
    let productList = [...productArray].filter(product => product && typeof product === 'object' && product.id);

    // Search filtering is now done server-side

    // Price range filter
    if (filters.priceMin !== '') {
      productList = productList.filter(p => parseFloat(p.price || 0) >= parseFloat(filters.priceMin));
    }
    if (filters.priceMax !== '') {
      productList = productList.filter(p => parseFloat(p.price || 0) <= parseFloat(filters.priceMax));
    }

    // Date range filter
    if (filters.dateFrom) {
      productList = productList.filter(p => new Date(p.date_added) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      productList = productList.filter(p => new Date(p.date_added) <= new Date(filters.dateTo));
    }

    // Platform filter - show items NOT on selected platforms
    if (filters.platforms.length > 0) {
      productList = productList.filter(p => {
        const listings = p.listings || {};
        return filters.platforms.some(platform => !listings[platform]);
      });
    }

    // Not Listed On filter - show items that are NOT listed on selected platforms
    if (filters.notOnPlatforms && filters.notOnPlatforms.length > 0) {
      productList = productList.filter(p => {
        const listings = p.listings || {};
        return filters.notOnPlatforms.every(platform => !listings[platform] || !listings[platform].id);
      });
    }

    // Sort products
    productList.sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
        case "price_asc":
          return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        case "price_desc":
          return parseFloat(b.price || 0) - parseFloat(a.price || 0);
        case "date_asc":
          const aDate = getSourceCreationDate(a) || new Date(a.date_added || 0);
          const bDate = getSourceCreationDate(b) || new Date(b.date_added || 0);
          return aDate - bDate;
        case "date_desc":
        default:
          const aDateDesc = getSourceCreationDate(a) || new Date(a.date_added || 0);
          const bDateDesc = getSourceCreationDate(b) || new Date(b.date_added || 0);
          return bDateDesc - aDateDesc;
      }
    });

    return productList;
  }, [products, sortBy, filters]);

  // Keyboard shortcuts
  const shortcuts = useMemo(() => ({
    'ctrl+a': () => {
      // Select all visible products
      setSelectedIds(filteredAndSortedProducts.filter(p => p && p.id).map(p => p.id));
    },
    'ctrl+d': () => {
      // Deselect all
      setSelectedIds([]);
    },
    'delete': () => {
      // Delete selected products
      if (selectedIds.length > 0) {
        setShowDeleteModal(true);
      }
    },
    'ctrl+e': () => {
      // Export CSV - use ALL products matching filter, not just current page
      const allFilteredProducts = allProducts
        .filter(product => product.status === activeTab)
        .filter(product => {
          if (!debouncedSearchTerm) return true;
          const term = debouncedSearchTerm.toLowerCase();
          return (
            product.name?.toLowerCase().includes(term) ||
            product.sku?.toLowerCase().includes(term)
          );
        });
      
      if (allFilteredProducts.length > 0) {
        exportToCSV(allFilteredProducts, `${activeTab}-products-${new Date().toISOString().split('T')[0]}.csv`, showError);
      } else {
        showWarning('No products to export', 'Export Warning');
      }
    },
    'ctrl+i': () => {
      // Import CSV
      setIsImportModalOpen(true);
    },
    'ctrl+f': () => {
      // Focus search
      document.querySelector('.search-input')?.focus();
    },
    'ctrl+shift+f': () => {
      // Toggle advanced filters
      setShowAdvancedFilters(!showAdvancedFilters);
    },
    'escape': () => {
      // Close modals or clear selection
      if (isUpdateModalOpen) setIsUpdateModalOpen(false);
      else if (showDeleteModal) setShowDeleteModal(false);
      else if (isImportModalOpen) setIsImportModalOpen(false);
      else if (selectedIds.length > 0) setSelectedIds([]);
      else if (showAdvancedFilters) setShowAdvancedFilters(false);
    }
  }), [filteredAndSortedProducts, allProducts, activeTab, selectedIds, debouncedSearchTerm, isUpdateModalOpen, showDeleteModal, isImportModalOpen, showAdvancedFilters]);

  useKeyboardShortcuts(shortcuts);

  const handleBulkAction = useCallback(
    async (actionType, platforms = []) => {
      if (selectedIds.length === 0) return;

      setIsPerformingAction(true);
      const selectedCount = selectedIds.length;
      
      try {
        const response = await apiClient.post("/api/products/bulk_action/", {
          ids: selectedIds,
          action: actionType,
          platforms: platforms,
        });
        
        // Clear selections immediately to prevent UI issues
        setSelectedIds([]);
        
        // Show success message
        let successMessage;
        switch (actionType) {
          case 'mark_as_active':
            successMessage = `${selectedCount} product${selectedCount > 1 ? 's' : ''} marked as active`;
            break;
          case 'mark_as_sold':
            successMessage = `${selectedCount} product${selectedCount > 1 ? 's' : ''} marked as sold`;
            break;
          case 'delete':
            successMessage = `${selectedCount} product${selectedCount > 1 ? 's' : ''} deleted`;
            break;
          default:
            successMessage = `Bulk action completed on ${selectedCount} product${selectedCount > 1 ? 's' : ''}`;
        }
        showSuccess(successMessage);
        
        // Refresh products immediately
        onProductsChange();
        
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Failed to perform action";
        showError(`Failed to perform bulk action: ${errorMessage}`, 'Bulk Action Failed');
      } finally {
        setIsPerformingAction(false);
      }
    },
    [selectedIds, onProductsChange, setIsPerformingAction, showSuccess, showError]
  );

  const handleDeleteConfirm = () => {
    handleBulkAction("delete");
    setShowDeleteModal(false);
  };

  const handleImport = async (importedProducts) => {
    for (const product of importedProducts) {
      try {
        await apiClient.post('/api/products/', product);
      } catch (error) {
        console.error('Failed to import product:', product.name, error);
      }
    }
    onProductsChange();
  };


  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(
    (e) => {
      debugLog('DEBUG: Select All clicked', {
        checked: e.target.checked,
        allProductsLength: allProducts.length,
        activeTab,
        searchTerm: debouncedSearchTerm
      });
      
      // Select ALL products matching current filter, not just current page
      const allFilteredProducts = allProducts
        .filter(product => product && product.status === activeTab)
        .filter(product => {
          if (!debouncedSearchTerm) return true;
          const term = debouncedSearchTerm.toLowerCase();
          return (
            product.name?.toLowerCase().includes(term) ||
            product.sku?.toLowerCase().includes(term)
          );
        });
      
      debugLog('DEBUG: Filtered products for select all:', allFilteredProducts.length);
      
      const idsToSelect = e.target.checked 
        ? allFilteredProducts.filter(p => p && p.id).map((p) => p.id)
        : [];
      
      debugLog('DEBUG: IDs to select:', idsToSelect.length);
      setSelectedIds(idsToSelect);
    },
    [allProducts, activeTab, debouncedSearchTerm]
  );

  const handleColumnToggle = useCallback((column) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setShowDisplayMenu(false);
    if (showDisplayMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showDisplayMenu]);

  const colSpan = 7 + Object.values(visibleColumns).filter(Boolean).length;

  return (
    <div>
      {isUpdateModalOpen && (
        <UpdateStatusModal
          products={selectedIds.map((id) => products.find((p) => p && p.id === id)).filter(Boolean)}
          onClose={() => setIsUpdateModalOpen(false)}
          onConfirm={handleBulkAction}
          isLoading={isPerformingAction}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          title="Delete Products"
          message={`Are you sure you want to permanently delete ${selectedIds.length} selected product(s)?

⚠️ This will:
• Remove the products from your dashboard permanently
• Delist them from all connected platforms (WooCommerce, eBay, Etsy, Vinted)
• Cannot be undone

If you just want to remove from platforms but keep in dashboard, use "Update Status" instead.`}
          confirmText="Yes, Permanently Delete & Delist"
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteModal(false)}
          isDestructive={true}
        />
      )}

      {isImportModalOpen && (
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImport}
          showError={showError}
        />
      )}

      <div className="controls-container">
        <div className="search-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search name or SKU..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsUserTyping(true);
              
              // Clear existing timeout
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }
              
              // Set user as not typing after 1 second of inactivity
              typingTimeoutRef.current = setTimeout(() => {
                setIsUserTyping(false);
              }, 1000);
            }}
            className="search-input"
            aria-label="Search products"
            autoComplete="off"
            spellCheck="false"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="clear-search"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="select-input"
          aria-label="Sort products"
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
          <option value="price_asc">Price (Low to High)</option>
          <option value="price_desc">Price (High to Low)</option>
        </select>

        <div className="display-menu-container">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDisplayMenu(!showDisplayMenu);
            }}
            className="display-button"
            aria-expanded={showDisplayMenu}
            aria-haspopup="true"
          >
            Display
          </button>
          {showDisplayMenu && (
            <div className="display-menu" onClick={(e) => e.stopPropagation()}>
              {Object.entries(visibleColumns).map(([column, isVisible]) => (
                <label key={column}>
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => handleColumnToggle(column)}
                  />
                  {column
                    .replace("_", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </label>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={`button-secondary ${showAdvancedFilters ? 'active' : ''}`}
          title="Toggle advanced filters (Ctrl+Shift+F)"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            background: showAdvancedFilters ? 'var(--color-primary)' : 'var(--color-surface)',
            color: showAdvancedFilters ? 'white' : 'var(--color-text-primary)',
            border: `1px solid ${showAdvancedFilters ? 'var(--color-primary)' : 'var(--color-border)'}`,
            boxShadow: showAdvancedFilters ? 'var(--shadow-sm)' : 'none'
          }}
        >
          Filters
          {showAdvancedFilters && <span style={{ fontSize: '12px', opacity: 0.8 }}>●</span>}
        </button>
      </div>

      {showAdvancedFilters && (
        <div style={{ 
          background: 'var(--color-surface)', 
          border: '1px solid var(--color-border)', 
          borderRadius: 'var(--border-radius-lg)', 
          padding: '24px', 
          marginBottom: '16px',
          boxShadow: 'var(--shadow-sm)',
          animation: 'slideDown 0.2s ease'
        }}>
          {/* Top row - Price and Date */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '24px',
            marginBottom: '24px'
          }}>
            {/* Price Range */}
            <div>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px', 
              fontWeight: '600', 
              marginBottom: '8px',
              color: 'var(--color-text-primary)'
            }}>
              Price Range
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                placeholder="Min £"
                value={filters.priceMin}
                onChange={(e) => setFilters(prev => ({ ...prev, priceMin: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--border-radius)',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  transition: 'border-color 0.2s ease',
                  ':focus': {
                    borderColor: 'var(--color-primary)',
                    outline: 'none'
                  }
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
              <input
                type="number"
                placeholder="Max £"
                value={filters.priceMax}
                onChange={(e) => setFilters(prev => ({ ...prev, priceMax: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--border-radius)',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px', 
              fontWeight: '600', 
              marginBottom: '8px',
              color: 'var(--color-text-primary)'
            }}>
              Date Range
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--border-radius)',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
              <span style={{ 
                color: 'var(--color-text-secondary)', 
                fontSize: '14px',
                fontWeight: '500'
              }}>to</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--border-radius)',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
            </div>
          </div>
          </div>

          {/* Platform filters row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '24px',
            marginBottom: '24px'
          }}>
          {/* Filter by Platform */}
          <div>
            <label style={{ 
              display: 'block',
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '12px',
              color: 'var(--color-text-primary)'
            }}>
              Filter by Platform
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '12px' 
            }}>
              {[
                { key: 'woocommerce', name: 'WooCommerce' },
                { key: 'ebay', name: 'eBay' },
                { key: 'etsy', name: 'Etsy' },
                { key: 'vinted', name: 'Vinted' }
              ].map(platform => (
                <label 
                  key={platform.key} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '8px 10px',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--color-border)',
                    background: filters.platforms.includes(platform.key) 
                      ? 'var(--color-primary-light)' 
                      : 'var(--color-surface)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!filters.platforms.includes(platform.key)) {
                      e.target.style.background = 'var(--color-surface-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!filters.platforms.includes(platform.key)) {
                      e.target.style.background = 'var(--color-surface)';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={filters.platforms.includes(platform.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters(prev => ({ ...prev, platforms: [...prev.platforms, platform.key] }));
                      } else {
                        setFilters(prev => ({ ...prev, platforms: prev.platforms.filter(p => p !== platform.key) }));
                      }
                    }}
                    style={{
                      accentColor: 'var(--color-primary)',
                      width: '16px',
                      height: '16px'
                    }}
                  />
                  <span style={{ 
                    color: filters.platforms.includes(platform.key) 
                      ? 'var(--color-primary)' 
                      : 'var(--color-text-primary)' 
                  }}>
                    {platform.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Not Listed On Filter Section */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px', 
              fontWeight: '600', 
              color: 'var(--color-text-primary)',
              fontSize: '14px' 
            }}>
              Not Listed On
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '12px' 
            }}>
              {[
                { key: 'woocommerce', name: 'WooCommerce' },
                { key: 'ebay', name: 'eBay' },
                { key: 'etsy', name: 'Etsy' },
                { key: 'vinted', name: 'Vinted' }
              ].map(platform => (
                <label 
                  key={platform.key} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '8px 10px',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--color-border)',
                    background: filters.notOnPlatforms?.includes(platform.key) 
                      ? 'var(--color-primary-light)' 
                      : 'var(--color-surface)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!filters.notOnPlatforms?.includes(platform.key)) {
                      e.target.style.background = 'var(--color-surface-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!filters.notOnPlatforms?.includes(platform.key)) {
                      e.target.style.background = 'var(--color-surface)';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={filters.notOnPlatforms?.includes(platform.key) || false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters(prev => ({ 
                          ...prev, 
                          notOnPlatforms: [...(prev.notOnPlatforms || []), platform.key] 
                        }));
                      } else {
                        setFilters(prev => ({ 
                          ...prev, 
                          notOnPlatforms: (prev.notOnPlatforms || []).filter(p => p !== platform.key) 
                        }));
                      }
                    }}
                    style={{
                      accentColor: 'var(--color-primary)',
                      width: '16px',
                      height: '16px'
                    }}
                  />
                  <span style={{ 
                    color: filters.notOnPlatforms?.includes(platform.key) 
                      ? 'var(--color-primary)' 
                      : 'var(--color-text-primary)' 
                  }}>
                    {platform.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
          </div>

          {/* Clear Filters button - full width */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button
              onClick={() => setFilters({ priceMin: '', priceMax: '', dateFrom: '', dateTo: '', platforms: [], notOnPlatforms: [] })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '500',
                padding: '10px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--border-radius)',
                background: 'var(--color-surface)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--color-surface-hover)';
                e.target.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'var(--color-surface)';
                e.target.style.color = 'var(--color-text-secondary)';
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {activeTab === 'sold' && (
              <button
                onClick={() => handleBulkAction('mark_as_active')}
                disabled={isPerformingAction}
                className="button-primary"
                title="Move selected items back to active status"
              >
                Move to Active
              </button>
            )}
            {activeTab !== 'sold' && (
              <button 
                onClick={() => {
                  // Open bulk crosslist modal for selected products
                  const selectedProducts = filteredAndSortedProducts.filter(p => p && selectedIds.includes(p.id));
                  if (selectedProducts.length > 0) {
                    setCrosslistModal({ isOpen: true, product: selectedProducts[0], bulkProducts: selectedProducts });
                  }
                }}
                disabled={isPerformingAction || selectedIds.length === 0} 
                className="button-primary"
                title="Cross-list selected products to other platforms"
              >
                Cross-list ({selectedIds.length})
              </button>
            )}
            <button
              onClick={() => setIsUpdateModalOpen(true)}
              disabled={isPerformingAction}
              className="button-secondary"
            >
              Delist from Platforms
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="button-danger"
              disabled={isPerformingAction}
            >
              Delete Selected Products
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="product-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  checked={(() => {
                    const allFilteredProducts = allProducts
                      .filter(product => product.status === activeTab)
                      .filter(product => {
                        if (!debouncedSearchTerm) return true;
                        const term = debouncedSearchTerm.toLowerCase();
                        return (
                          product.name?.toLowerCase().includes(term) ||
                          product.sku?.toLowerCase().includes(term)
                        );
                      });
                    return allFilteredProducts.length > 0 && selectedIds.length === allFilteredProducts.length;
                  })()}
                  aria-label="Select all products"
                />
              </th>
              <th>Product</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Status</th>
              <th>Platforms</th>
              {activeTab === 'action_required' && <th style={{ minWidth: '200px' }}>Action Required</th>}
              <th>Actions</th>
              {visibleColumns.date_added && <th>Date Added</th>}
              {visibleColumns.last_updated && <th>Last Updated</th>}
              {visibleColumns.date_sold && <th>Date Sold</th>}
              {visibleColumns.description && <th>Description</th>}
              {visibleColumns.product_id && <th>ID</th>}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedProducts.length > 0 ? (
              filteredAndSortedProducts.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  isSelected={selectedIds.includes(product.id)}
                  onSelectRow={handleSelectRow}
                  visibleColumns={visibleColumns}
                  onViewProduct={onEditProduct}
                  onCrossList={onCrossList}
                  onDelist={onDelist}
                  onMarkAsSold={onMarkAsSold}
                  onMarkAsActive={onMarkAsActive}
                  onDelete={onDelete}
                  onToggleActionsMenu={onToggleActionsMenu}
                  activeTab={activeTab}
                  platformSettings={platformSettings}
                />
              ))
            ) : (
              <tr>
                <td colSpan={colSpan} className="no-products-cell">
                  {debouncedSearchTerm
                    ? `No products found matching "${debouncedSearchTerm}"`
                    : "You have no products in this category."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Print-only footer */}
      <div className="print-info" style={{ display: 'none' }}>
        <p>
          CrossLister Dashboard - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Products
          <br />
          Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          <br />
          Total products shown: {filteredAndSortedProducts.length}
          {debouncedSearchTerm && ` | Filtered by: "${debouncedSearchTerm}"`}
        </p>
      </div>
    </div>
  );
}

function NotificationSystem({ notifications, removeNotification, cancelAction }) {
  if (!notifications || notifications.length === 0) return null;

  return (
    <div className="notification-container" style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 10000,
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      {notifications.map(notification => (
        <NotificationItem 
          key={notification.id} 
          notification={notification} 
          onRemove={() => removeNotification(notification.id)}
          onCancel={cancelAction}
        />
      ))}
    </div>
  );
}

function NotificationItem({ notification, onRemove, onCancel }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  const getNotificationStyle = (type) => {
    const baseStyle = {
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid',
      backgroundColor: 'var(--color-surface)',
      color: 'var(--color-text)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative'
    };

    switch (type) {
      case 'success':
        return { ...baseStyle, borderColor: '#10B981', backgroundColor: '#F0FDF4' };
      case 'error':
        return { ...baseStyle, borderColor: '#EF4444', backgroundColor: '#FEF2F2' };
      case 'warning':
        return { ...baseStyle, borderColor: '#F59E0B', backgroundColor: '#FFFBEB' };
      default:
        return { ...baseStyle, borderColor: '#6B7280', backgroundColor: 'var(--color-surface)' };
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  const shouldTruncate = notification.message.length > 100;
  const displayMessage = isExpanded || !shouldTruncate 
    ? notification.message 
    : notification.message.substring(0, 100) + '...';
  
  const hasErrorDetails = notification.error || notification.details;
  
  const handleMainClick = (e) => {
    // Only handle click if clicked outside of expand arrow or close button
    if (!e.target.closest('.expand-arrow') && !e.target.closest('.close-button')) {
      if (notification.productId) {
        // Open product details - this would need to be passed as a prop
        console.log('Open product details for:', notification.productId);
      } else if (shouldTruncate) {
        setIsExpanded(!isExpanded);
      }
    }
  };

  return (
    <div 
      style={{...getNotificationStyle(notification.type), cursor: notification.productId ? 'pointer' : shouldTruncate ? 'pointer' : 'default'}}
      onClick={handleMainClick}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '18px', flexShrink: 0 }}>
          {getIcon(notification.type)}
        </span>
        <div style={{ flex: 1 }}>
          {notification.title && (
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {notification.title}
            </div>
          )}
          <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
            {displayMessage}
          </div>
          {shouldTruncate && !isExpanded && (
            <div style={{ 
              fontSize: '12px', 
              color: 'var(--color-text-secondary)', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Click to expand full message
            </div>
          )}
          {hasErrorDetails && (
            <div 
              className="expand-arrow"
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(!showDetails);
              }}
              style={{
                marginTop: '8px',
                fontSize: '12px',
                color: 'var(--color-primary)',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <span style={{ transform: showDetails ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▶</span>
              {showDetails ? 'Hide' : 'Show'} error details
            </div>
          )}
          {showDetails && hasErrorDetails && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              color: 'var(--color-text-secondary)',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}>
              {notification.error || notification.details}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {notification.cancelable && onCancel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancel(notification.id);
              }}
              style={{
                background: 'var(--color-danger)',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '4px 8px',
                lineHeight: '1',
                whiteSpace: 'nowrap'
              }}
              title="Cancel action"
            >
              Cancel
            </button>
          )}
          <button
            className="close-button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            padding: '0',
            lineHeight: '1'
          }}
          title="Dismiss notification"
        >
          ×
        </button>
        </div>
      </div>
    </div>
  );
}

function QueueErrorDisplay({ error }) {
  const [showDetails, setShowDetails] = useState(false);
  
  if (!error) return null;
  
  const shouldTruncate = error.length > 50;
  const displayError = shouldTruncate && !showDetails 
    ? error.substring(0, 50) + '...' 
    : error;
  
  return (
    <div style={{ 
      fontSize: '11px', 
      color: 'var(--color-error)',
      marginTop: '4px'
    }}>
      <div>{displayError}</div>
      {shouldTruncate && (
        <div 
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
          style={{
            marginTop: '4px',
            fontSize: '10px',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span style={{ 
            transform: showDetails ? 'rotate(90deg)' : 'rotate(0deg)', 
            transition: 'transform 0.2s ease' 
          }}>▶</span>
          {showDetails ? 'Hide' : 'Show'} full error
        </div>
      )}
    </div>
  );
}


function App() {
  const [token, setToken] = useState(localStorage.getItem("authToken"));
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  
  // Removed complex caching that was causing issues
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [selectedPolicies, setSelectedPolicies] = useState({
    ebayPayment: '',
    ebayShipping: '',
    ebayReturn: '',
    etsyShipping: '',
    etsyReturn: ''
  });

  // Platform visibility settings
  const [platformSettings, setPlatformSettings] = useState(() => {
    const saved = localStorage.getItem('platformSettings');
    return saved ? JSON.parse(saved) : {
      woocommerce: { enabled: true, name: 'WooCommerce' },
      ebay: { enabled: true, name: 'eBay' },
      etsy: { enabled: true, name: 'Etsy' },
      vinted: { enabled: true, name: 'Vinted' }
    };
  });

  // Source platform setting - persists across sessions
  const [sourcePlatform, setSourcePlatform] = useState(() => {
    const saved = localStorage.getItem('crosslister_sourcePlatform');
    return saved || 'woocommerce';
  });

  // Debounced tab switching to prevent fast clicking issues
  const [tabSwitchTimeout, setTabSwitchTimeout] = useState(null);
  const debouncedTabSwitch = useCallback((newTab) => {
    if (tabSwitchTimeout) {
      clearTimeout(tabSwitchTimeout);
    }
    const timeout = setTimeout(() => {
      setActiveTab(newTab);
      setTabSwitchTimeout(null);
    }, 100); // 100ms debounce
    setTabSwitchTimeout(timeout);
  }, [tabSwitchTimeout]);

  // Save platform settings to localStorage when changed
  useEffect(() => {
    localStorage.setItem('platformSettings', JSON.stringify(platformSettings));
  }, [platformSettings]);

  // Save source platform to localStorage when changed
  useEffect(() => {
    localStorage.setItem('crosslister_sourcePlatform', sourcePlatform);
  }, [sourcePlatform]);

  // Notification utility functions
  const addNotification = useCallback((type, message, title = null, duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = { id, type, message, title };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after duration (except for errors which stay longer)
    if (type !== 'error') {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    } else {
      // Errors stay for 10 seconds
      setTimeout(() => {
        removeNotification(id);
      }, 10000);
    }
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const cancelAction = useCallback((notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && notification.cancelAction) {
      // Execute the cancel function
      notification.cancelAction();
      // Remove the notification
      removeNotification(notificationId);
      // Show cancel confirmation
      addNotification('info', 'Action cancelled successfully', 'Action Cancelled');
    }
  }, [notifications, removeNotification, addNotification]);

  const showSuccess = useCallback((message, title = null) => {
    addNotification('success', message, title);
  }, [addNotification]);

  const showError = useCallback((message, title = null) => {
    addNotification('error', message, title);
  }, [addNotification]);

  const showWarning = useCallback((message, title = null) => {
    addNotification('warning', message, title);
  }, [addNotification]);

  const showInfo = useCallback((message, title = null) => {
    addNotification('info', message, title);
  }, [addNotification]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSyncDropdownOpen, setIsSyncDropdownOpen] = useState(false);
  const syncButtonRef = useRef(null);
  const [crosslistModal, setCrosslistModal] = useState({ isOpen: false, product: null });
  const [isPerformingSync, setIsPerformingSync] = useState(false);
  const [syncingPlatform, setSyncingPlatform] = useState('');
  const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const toolsButtonRef = useRef(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  
  // Global dropdown menu state
  const [globalDropdown, setGlobalDropdown] = useState({
    isVisible: false,
    position: { top: 0, left: 0 },
    product: null
  });
  
  // Toggle actions menu function
  const handleToggleActionsMenu = (product, position) => {
    setGlobalDropdown(prev => ({
      isVisible: !prev.isVisible || prev.product?.id !== product.id,
      position: position,
      product: product
    }));
  };
  
  // Close actions menu function
  const handleCloseActionsMenu = () => {
    setGlobalDropdown({
      isVisible: false,
      position: { top: 0, left: 0 },
      product: null
    });
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Back to original fast setting
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600);
  
  
  // Fetch sales statistics
  const fetchSalesStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/sales-statistics/');
      setSalesStats(response.data);
    } catch (error) {
      console.error('Failed to fetch sales statistics:', error);
    }
  }, []);
  
  // Sale detection state
  const [saleDetectionResults, setSaleDetectionResults] = useState(null);
  const [isRunningSaleDetection, setIsRunningSaleDetection] = useState(false);
  const [newSales, setNewSales] = useState(() => {
    // CLEAR ALL STORED SALES NOTIFICATIONS - they're causing too many issues
    localStorage.removeItem('crosslister_newSales');
    console.log('🗑️ Cleared ALL stored sales notifications to prevent duplicates');
    return [];
  });
  const [showSalesDropdown, setShowSalesDropdown] = useState(false);
  const [crosslistQueue, setCrosslistQueue] = useState(() => {
    try {
      const saved = localStorage.getItem('crosslister_crosslistQueue');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [delistQueue, setDelistQueue] = useState(() => {
    try {
      const saved = localStorage.getItem('crosslister_delistQueue');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Combined notifications panel state
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState('queue');

  // Individual product delete modal state
  const [showIndividualDeleteModal, setShowIndividualDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Individual product delist modal state
  const [showIndividualDelistModal, setShowIndividualDelistModal] = useState(false);
  const [productToDelist, setProductToDelist] = useState(null);

  // Sales statistics state
  const [salesStats, setSalesStats] = useState({
    today_sales: { value: '£0.00', count: 0 },
    monthly_sales: { value: '£0.00', count: 0 },
    total_sales: { value: '£0.00', count: 0 },
    product_counts: { active: 0, sold: 0, inactive: 0, action_required: 0 }
  });

  // Always use light mode
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    localStorage.removeItem('darkMode'); // Clean up any existing dark mode preference
  }, []);

  // DISABLED: localStorage persistence was causing duplicate notifications
  // Sales notifications now only exist for the current session
  // useEffect(() => {
  //   localStorage.setItem('crosslister_newSales', JSON.stringify(newSales));
  // }, [newSales]);

  useEffect(() => {
    localStorage.setItem('crosslister_crosslistQueue', JSON.stringify(crosslistQueue));
  }, [crosslistQueue]);

  useEffect(() => {
    localStorage.setItem('crosslister_delistQueue', JSON.stringify(delistQueue));
  }, [delistQueue]);

  // Click outside handler for sales dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSalesDropdown && !event.target.closest('[data-sales-dropdown]')) {
        setShowSalesDropdown(false);
      }
    };
    
    if (showSalesDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSalesDropdown]);


  const productCounts = useMemo(() => {
    const counts = {
      active: allProducts.filter((p) => p.status === "active").length,
      sold: allProducts.filter((p) => p.status === "sold").length,
      inactive: allProducts.filter((p) => p.status === "inactive").length,
      action_required: allProducts.filter((p) => p.status === "action_required")
        .length,
    };
    console.log('DEBUG: Product counts calculated:', counts, 'from', allProducts.length, 'total products');
    return counts;
  }, [allProducts]);

  const fetchAllProducts = useCallback(async (force = false) => {
    if (!token) return;
    
    try {
      // Just fetch counts, not all products - much faster!
      // We only need the counts for the tab badges
      const [activeResponse, soldResponse, inactiveResponse, actionRequiredResponse] = await Promise.all([
        apiClient.get('/api/products/?status=active&page_size=1'),
        apiClient.get('/api/products/?status=sold&page_size=1'),
        apiClient.get('/api/products/?status=inactive&page_size=1'), 
        apiClient.get('/api/products/?status=action_required&page_size=1')
      ]);

      // Create a minimal allProducts array with just counts
      // This is used for the tab counters
      const mockAllProducts = [
        ...Array(activeResponse.data.count || 0).fill({status: 'active'}),
        ...Array(soldResponse.data.count || 0).fill({status: 'sold'}),
        ...Array(inactiveResponse.data.count || 0).fill({status: 'inactive'}),
        ...Array(actionRequiredResponse.data.count || 0).fill({status: 'action_required'})
      ];
      
      setAllProducts(mockAllProducts);
    } catch (err) {
      // Failed to fetch product counts
    }
  }, [token]);

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setError("");
    setIsLoading(true);
    try {
      // Ensure page is valid (at least 1)
      const safePage = Math.max(1, currentPage);
      
      // Build search params
      const params = new URLSearchParams({
        status: activeTab,
        page: safePage,
        page_size: itemsPerPage
      });
      
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      console.log(`DEBUG: About to fetch products with params:`, params.toString());
      const startTime = performance.now();
      const response = await apiClient.get(`/api/products/?${params.toString()}`);
      const endTime = performance.now();
      console.log(`🚀 PERFORMANCE: API call took ${(endTime - startTime).toFixed(2)}ms`);
      
      // Add additional validation to prevent crashes
      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }
      if (response.data?.results) {
        const statusCounts = response.data.results.reduce((acc, product) => {
          acc[product.status] = (acc[product.status] || 0) + 1;
          return acc;
        }, {});
        console.log(`DEBUG: Product status breakdown for ${activeTab}:`, statusCounts);
        
        if (activeTab === 'action_required' && response.data.results.length > 0) {
          console.log(`DEBUG: Action required products:`, response.data.results.map(p => ({
            name: p.name,
            sku: p.sku,
            status: p.status,
            id: p.id
          })));
        }
      }

      const productData = response.data;
      if (Array.isArray(productData)) {
        // Non-paginated response (fallback)
        setProducts(productData);
        setTotalItems(productData.length);
        setTotalPages(1);
      } else if (productData && Array.isArray(productData.results)) {
        // Paginated response
        console.log(`DEBUG: Setting products for ${activeTab}:`, productData.results.length, 'products');
        console.log(`DEBUG: First product:`, productData.results[0]);
        setProducts(productData.results);
        setTotalItems(productData.count || 0);
        const calculatedPages = Math.ceil((productData.count || 0) / itemsPerPage);
        // If we're on a page beyond what the data suggests, adjust
        const actualPages = Math.max(1, calculatedPages);
        setTotalPages(actualPages);
        
        // If current page is beyond actual pages, reset to last valid page
        if (currentPage > actualPages && actualPages > 0) {
          setCurrentPage(actualPages);
        }
      } else {
        console.error("Unexpected API response format:", productData);
        setProducts([]);
        setError("Unexpected response format from server");
        return;
      }
      
    } catch (err) {
      // Fetch products error - prevent crashes with better error handling
      console.error('fetchProducts error:', err);
      setProducts([]);
      
      // More robust error message handling with timeout detection
      let errorMessage = `Could not fetch ${activeTab} products.`;
      try {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage += ' Request timed out. This may be due to background sales detection running. Please try again.';
        } else if (err.response?.data?.detail) {
          errorMessage += ` ${err.response.data.detail}`;
        } else if (err.message) {
          errorMessage += ` ${err.message}`;
        } else {
          errorMessage += ' Unknown error occurred.';
        }
      } catch (msgErr) {
        console.error('Error processing error message:', msgErr);
        errorMessage += ' An unexpected error occurred.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, itemsPerPage, debouncedSearchTerm, activeTab]);

  // Load saved policies on app startup
  const loadGlobalPolicies = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/credentials/');
      if (response.data && response.data.pricing_rules) {
        const ebaySettings = response.data.pricing_rules.ebay_settings || {};
        const etsySettings = response.data.pricing_rules.etsy_settings || {};
        
        setSelectedPolicies(prev => ({
          ...prev,
          ebayPayment: ebaySettings.payment_policy || '',
          ebayShipping: ebaySettings.shipping_policy || '',
          ebayReturn: ebaySettings.return_policy || '',
          etsyShipping: etsySettings.shipping_profile || '',
          etsyReturn: etsySettings.return_policy || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load global policies:', error);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchAllProducts();
      fetchProducts();
      fetchSalesStats();
      loadGlobalPolicies(); // Load policies on app startup
    }
  }, [token, activeTab, loadGlobalPolicies]);

  // Removed redundant fetchProducts useEffect to prevent multiple calls

  // Automatic sales detection - runs every 5 minutes
  useEffect(() => {
    if (!token) return;
    
    const runAutoSalesDetection = async () => {
      // Skip auto-detection if manual detection is running to prevent duplicates
      if (isRunningSaleDetection) {
        return;
      }
      
      try {
        const response = await apiClient.post('/api/sale-detection/run/');
        if (response.data.success && response.data.results.summary.total_sales > 0) {
          // Refresh products if sales were detected
          fetchProducts();
          fetchAllProducts();
          
          // Only process sales if there are actually new ones
          if (response.data.results.delisting_results && response.data.results.delisting_results.length > 0) {
            // Blacklist problematic SKUs that keep appearing
            const blacklistedSkus = ['A62424', 'A96758', '973654-1'];
            
            const sales = response.data.results.delisting_results
              .filter(result => !blacklistedSkus.includes(result.sku)) // Filter out blacklisted
              .map(result => ({
                productName: result.product_name || result.sku,
                platform: result.sold_on_platform || 'unknown',
                sku: result.sku,
                salePrice: result.sale_price || 0,
                saleDate: new Date().toLocaleString(),
                timestamp: Date.now(),
                detectionId: `${result.sku}-${Date.now()}` // Unique ID for this detection
              }));
            
            if (sales.length > 0) {
              setNewSales(prev => {
                // More robust duplicate checking - check SKU and recent timestamps
                const cutoffTime = Date.now() - (10 * 60 * 1000); // 10 minutes ago
                const recentSales = prev.filter(sale => sale.timestamp > cutoffTime);
                const existingSkus = recentSales.map(sale => sale.sku);
                const newUniqueSales = sales.filter(sale => !existingSkus.includes(sale.sku));
                
                // Only add if we have genuinely new sales
                if (newUniqueSales.length > 0) {
                  console.log(`🎉 Adding ${newUniqueSales.length} new sales notifications`);
                  return [...prev, ...newUniqueSales];
                }
                return prev;
              });
            }
          }
        }
      } catch (error) {
        // Silent fail for auto detection - don't show errors to user
        console.log('Auto sales detection failed:', error);
      }
    };

    // Don't run immediately on page load - only run on interval
    // Run immediately on startup (after a 5 second delay to let everything load)
    const initialTimeout = setTimeout(runAutoSalesDetection, 5000);
    
    // Then run every 15 minutes
    const interval = setInterval(runAutoSalesDetection, 15 * 60 * 1000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [token]); // Removed function dependencies to prevent cascading calls

  // Reset to first page when changing tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);
  
  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch products when pagination or search changes
  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  // Fetch products when activeTab changes - merged with main useEffect

  const handleLogin = (userToken) => {
    localStorage.setItem("authToken", userToken);
    setToken(userToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    setToken(null);
    setProducts([]);
    setAllProducts([]);
    setActiveTab("active");
    setError("");
  };

  const handleWooCommerceSync = async () => {
    setIsPerformingSync(true);
    setSyncingPlatform('woocommerce');
    document.body.style.cursor = 'wait';
    try {
      const response = await apiClient.post('/api/sync/woocommerce/');
      if (response.data.success) {
        // Show sync completion notification with counts
        const { created, updated, total_processed } = response.data;
        const details = [];
        if (created > 0) details.push(`${created} created`);
        if (updated > 0) details.push(`${updated} updated`);
        const summaryText = details.length > 0 ? ` (${details.join(', ')})` : '';
        showSuccess(`WooCommerce sync completed! Processed ${total_processed} products${summaryText}.`, 'Sync Complete');
        
        fetchProducts(); // Refresh products
        fetchAllProducts(); // Refresh all products for counts
      } else {
        showError(`Sync failed: ${response.data.error || 'Unknown error'}`, 'Sync Failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Sync failed';
      showError(`WooCommerce sync failed: ${errorMessage}`, 'WooCommerce Sync Failed');
    } finally {
      setIsPerformingSync(false);
      setSyncingPlatform('');
      setIsSyncDropdownOpen(false);
      document.body.style.cursor = 'default';
    }
  };

  const handleEbaySync = async () => {
    setIsPerformingSync(true);
    setSyncingPlatform('ebay');
    document.body.style.cursor = 'wait';
    try {
      const response = await apiClient.post('/api/sync/ebay/');
      if (response.data.success) {
        // Show sync completion notification with counts
        const { created, updated, total_processed } = response.data;
        const details = [];
        if (created > 0) details.push(`${created} created`);
        if (updated > 0) details.push(`${updated} updated`);
        const summaryText = details.length > 0 ? ` (${details.join(', ')})` : '';
        showSuccess(`eBay sync completed! Processed ${total_processed} products${summaryText}.`, 'Sync Complete');
        
        fetchProducts(); // Refresh products
        fetchAllProducts(); // Refresh all products for counts
      } else {
        showError(`Sync failed: ${response.data.error || 'Unknown error'}`, 'Sync Failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Sync failed';
      showError(`eBay sync failed: ${errorMessage}`, 'eBay Sync Failed');
    } finally {
      setIsPerformingSync(false);
      setSyncingPlatform('');
      setIsSyncDropdownOpen(false);
      document.body.style.cursor = 'default';
    }
  };

  const handleEtsySync = async () => {
    setIsPerformingSync(true);
    setSyncingPlatform('etsy');
    document.body.style.cursor = 'wait';
    try {
      const response = await apiClient.post('/api/sync/etsy/');
      if (response.data.success) {
        // Show sync completion notification with counts
        const { created, updated, total_processed } = response.data;
        const details = [];
        if (created > 0) details.push(`${created} created`);
        if (updated > 0) details.push(`${updated} updated`);
        const summaryText = details.length > 0 ? ` (${details.join(', ')})` : '';
        showSuccess(`Etsy sync completed! Processed ${total_processed} products${summaryText}.`, 'Sync Complete');
        
        fetchProducts(); // Refresh products
        fetchAllProducts(); // Refresh all products for counts
      } else {
        showError(`Sync failed: ${response.data.error || 'Unknown error'}`, 'Sync Failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Sync failed';
      showError(`Etsy sync failed: ${errorMessage}`, 'Etsy Sync Failed');
    } finally {
      setIsPerformingSync(false);
      setSyncingPlatform('');
      setIsSyncDropdownOpen(false);
      document.body.style.cursor = 'default';
    }
  };

  const handleCrossList = (product) => {
    // Open crosslist modal for the product
    setCrosslistModal({ isOpen: true, product });
  };

  const handleDelist = async (product) => {
    // Show the individual delist modal instead of directly delisting
    setProductToDelist(product);
    setShowIndividualDelistModal(true);
  };

  const handleMarkAsSold = async (product) => {
    try {
      setIsPerformingAction(true);
      
      // Use the new mark as sold endpoint that handles both status update and WooCommerce sync
      const soldResponse = await apiClient.post(`/api/products/${product.id}/mark_as_sold/`, {}, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Then delist from all platforms
      const response = await apiClient.post('/api/delist/manual/', {
        product_id: product.id,
        platforms: ['woocommerce', 'ebay', 'etsy', 'vinted']
      });

      // Optimistically update the product status
      setProducts(prevProducts => prevProducts.map(p => {
        if (p.id === product.id) {
          const updatedListings = { ...p.listings };
          // Mark delisted platforms as delisted
          const platforms = response.data?.results?.platforms_delisted || [];
          platforms.forEach(platform => {
            if (updatedListings[platform]) {
              updatedListings[platform] = {
                ...updatedListings[platform],
                status: 'delisted',
                delisted_at: new Date().toISOString()
              };
            }
          });
          return { ...p, status: 'sold', listings: updatedListings };
        }
        return p;
      }));
      
      // Refresh products and sales stats to show updated status
      fetchProducts();
      fetchAllProducts();
      fetchSalesStats();
      
      // Show success message
      const platforms = response.data?.results?.platforms_delisted || [];
      showSuccess(`Successfully marked ${product.name} as sold and delisted from: ${platforms.join(', ')}`, 'Product Marked as Sold');
      
    } catch (error) {
      console.error('Mark as sold error:', error);
      showError('Failed to mark as sold. Please try again.', 'Mark as Sold Failed');
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleMarkAsActive = async (product) => {
    try {
      setIsPerformingAction(true);
      
      // Use the new mark as active endpoint that handles both status update and WooCommerce sync
      await apiClient.post(`/api/products/${product.id}/mark_as_active/`, {}, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Optimistically update the product status
      setProducts(prevProducts => prevProducts.map(p => {
        if (p.id === product.id) {
          return { ...p, status: 'active' };
        }
        return p;
      }));
      
      // Refresh products and sales stats to show updated status
      fetchProducts();
      fetchAllProducts();
      fetchSalesStats();
      
      showSuccess(`Successfully marked ${product.name} as active for relisting.`, 'Product Reactivated');
      
    } catch (error) {
      console.error('Mark as active error:', error);
      showError('Failed to mark as active. Please try again.', 'Mark as Active Failed');
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleDelete = async (product) => {
    // Use the new individual delete confirmation modal
    setProductToDelete(product);
    setShowIndividualDeleteModal(true);
  };

  const handleIndividualDeleteConfirm = async () => {
    if (!productToDelete) return;
    
    try {
      setIsPerformingAction(true);
      await apiClient.delete(`/api/products/${productToDelete.id}/`);
      
      // Optimistically remove the product from the current list
      setProducts(prevProducts => prevProducts.filter(p => p.id !== productToDelete.id));
      
      // Refresh products to ensure consistency
      fetchProducts();
      fetchAllProducts();
      
      showSuccess(`Product "${productToDelete.name}" has been deleted successfully.`, 'Product Deleted');
      
      // Close modal and reset state
      setShowIndividualDeleteModal(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      showError('Failed to delete product. Please try again.', 'Delete Failed');
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleSaleDetection = async () => {
    setIsRunningSaleDetection(true);
    setSaleDetectionResults(null);
    
    try {
      const response = await apiClient.post('/api/sale-detection/run/');
      if (response.data.success) {
        setSaleDetectionResults(response.data.results);
        
        // If there are new sales detected, add them to the notifications
        if (response.data.results && response.data.results.details) {
          const newSalesFound = [];
          
          // Blacklist problematic SKUs that keep appearing
          const blacklistedSkus = ['A62424', 'A96758', '973654-1'];
          
          response.data.results.details.forEach(result => {
            if (result.sales_found && result.sales_found.length > 0) {
              result.sales_found.forEach(sale => {
                // Skip blacklisted SKUs
                if (!blacklistedSkus.includes(sale.sku)) {
                  newSalesFound.push({
                    productName: sale.product_name || 'Unknown Product',
                    platform: result.platform,
                    sku: sale.sku,
                    salePrice: sale.sale_price || 0,
                    saleDate: new Date(sale.sale_date || Date.now()).toLocaleDateString(),
                    timestamp: Date.now()
                  });
                }
              });
            }
          });
          
          if (newSalesFound.length > 0) {
            setNewSales(prev => {
              // Filter out any sales that already exist (same SKU)
              const existingSkus = prev.map(sale => sale.sku);
              const uniqueNewSales = newSalesFound.filter(sale => !existingSkus.includes(sale.sku));
              return [...uniqueNewSales, ...prev];
            });
          }
        }
        
        // Refresh products to show updated statuses
        fetchProducts();
        fetchAllProducts(true);
        
        const summary = response.data.results.summary;
        showSuccess(`Sale detection completed!\n${summary.total_sales} sales found\n${summary.successful_delistings} successful delistings\n${summary.failed_delistings} failed delistings`, 'Sale Detection Complete');
      } else {
        showError(`Sale detection failed: ${response.data.error || 'Unknown error'}`, 'Sale Detection Failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Sale detection failed';
      showError(`Sale detection failed: ${errorMessage}`, 'Sale Detection Failed');
    } finally {
      setIsRunningSaleDetection(false);
    }
  };

  // Function to add sample sales for demonstration
  const addSampleSales = () => {
    const sampleSales = [
      {
        productName: 'Vintage Leather Jacket',
        platform: 'WooCommerce',
        sku: 'VLJ-001',
        salePrice: 85.99,
        saleDate: new Date().toLocaleDateString(),
        timestamp: Date.now()
      },
      {
        productName: 'Handmade Ceramic Mug',
        platform: 'Etsy',
        sku: 'HCM-042',
        salePrice: 24.50,
        saleDate: new Date().toLocaleDateString(),
        timestamp: Date.now() - 1000
      }
    ];
    
    setNewSales(prev => [...sampleSales, ...prev]);
  };

  const handleImport = async (importData) => {
    // Handle different import types
    if (Array.isArray(importData)) {
      // Traditional CSV import - just products array
      let successCount = 0;
      let errorCount = 0;
      
      for (const product of importData) {
        try {
          await apiClient.post('/api/products/', product);
          successCount++;
        } catch (error) {
          console.error('Failed to import product:', product.name, error);
          errorCount++;
        }
      }
      
      // Add to notification queue
      setCrosslistQueue(prev => [...prev, {
        id: Date.now(),
        type: 'import',
        action: 'csv_import',
        status: errorCount === 0 ? 'completed' : 'partial',
        timestamp: new Date().toISOString(),
        message: `CSV Import: ${successCount} products imported${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        details: { successCount, errorCount, total: importData.length }
      }]);
      
    } else if (importData.type) {
      // Enhanced import with metadata
      const { type, platform, products, duplicatesFound, duplicatesHandled } = importData;
      
      // Add to notification queue
      setCrosslistQueue(prev => [...prev, {
        id: Date.now(),
        type: 'import',
        action: type,
        platform: platform,
        status: 'completed',
        timestamp: new Date().toISOString(),
        message: `${platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Import'}: ${products?.length || 0} products imported${duplicatesFound ? `, ${duplicatesFound} duplicates handled` : ''}`,
        details: { 
          importedCount: products?.length || 0, 
          duplicatesFound, 
          duplicatesHandled,
          duplicateAction: importData.duplicateAction || 'unknown'
        }
      }]);
    }
    
    fetchProducts();
    fetchAllProducts();
  };

  const handlePageChange = (page) => {
    console.log(`DEBUG: Changing to page ${page}`);
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSaveProduct = async (productId, data) => {
    try {
      await apiClient.put(`/api/products/${productId}/`, data);
      fetchProducts();
      fetchAllProducts();
    } catch (err) {
      console.error("Failed to update product", err);
      throw err;
    }
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  // Validate that required policies are set before crosslisting
  const validatePoliciesForPlatform = (platformKey) => {
    if (platformKey === 'ebay') {
      const hasPayment = selectedPolicies.ebayPayment && selectedPolicies.ebayPayment.trim() !== '';
      const hasShipping = selectedPolicies.ebayShipping && selectedPolicies.ebayShipping.trim() !== '';
      const hasReturn = selectedPolicies.ebayReturn && selectedPolicies.ebayReturn.trim() !== '';
      
      if (!hasPayment || !hasShipping || !hasReturn) {
        const missingPolicies = [];
        if (!hasPayment) missingPolicies.push('Payment Policy');
        if (!hasShipping) missingPolicies.push('Shipping Policy');
        if (!hasReturn) missingPolicies.push('Return Policy');
        
        showError(
          `eBay crosslisting requires the following policies to be configured:\n• ${missingPolicies.join('\n• ')}\n\nPlease go to Settings → Default Policies to configure them.`,
          'eBay Policies Required'
        );
        return false;
      }
    } else if (platformKey === 'etsy') {
      // Skip shipping profile check if using extension (browser automation)
      // The extension will handle Etsy crosslisting without API requirements
      if (!window.CrossListerExtension || !window.CrossListerExtension.isInstalled) {
        // Only require shipping profile for API-based crosslisting
        const hasShipping = selectedPolicies.etsyShipping && selectedPolicies.etsyShipping.trim() !== '';
        
        if (!hasShipping) {
          showError(
            `Etsy API crosslisting requires a Shipping Profile to be configured.\n\nAlternatively, install the CrossLister Extension to bypass this requirement.`,
            'Etsy Shipping Profile Required'
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleCrosslistProduct = async (product, platformKey) => {
    console.log('🚨 DEBUG: handleCrosslistProduct called with platform:', platformKey);
    console.log('🚨 DEBUG: Product name:', product.name);
    
    // Validate policies before crosslisting
    if (!validatePoliciesForPlatform(platformKey)) {
      return; // Don't proceed with crosslisting
    }

    // Add to crosslist queue
    const queueItem = {
      id: `${product.id}-${platformKey}-${Date.now()}`,
      product: product,
      platform: platformKey,
      status: 'processing',
      timestamp: new Date().toISOString()
    };
    setCrosslistQueue(prev => [...prev, queueItem]);
    
    try {
      let response;
      if (platformKey === 'vinted') {
        // Vinted uses extension directly - no API needed
        console.log('🔍 Checking for Vinted extension...', window.CrossListerExtension);
        
        if (window.CrossListerExtension && window.CrossListerExtension.isInstalled) {
          console.log('✅ Extension detected! Calling crosslistToVinted...');
          try {
            // Get pricing rules from API
            let vintedMarkup = 0;
            let roundTo99 = false;
            try {
              const credsResponse = await apiClient.get('/api/credentials/');
              vintedMarkup = credsResponse.data?.pricing_rules?.vinted_markup || 0;
              roundTo99 = credsResponse.data?.pricing_rules?.round_to_99 || false;
            } catch (error) {
              console.warn('Could not fetch pricing rules, using defaults:', error);
            }
            let vintedPrice = parseFloat(product.price) * (1 + vintedMarkup / 100);
            
            // Round to .99 if enabled
            if (roundTo99) {
              vintedPrice = Math.floor(vintedPrice) + 0.99;
            }
            
            // Debug product images before sending to extension
            console.log('🖼️ DEBUG: product.images:', product.images);
            console.log('🖼️ DEBUG: product.images type:', typeof product.images);
            console.log('🖼️ DEBUG: product.images length:', product.images?.length);
            console.log('🖼️ DEBUG: Full product object:', product);
            
            const imagesToSend = product.images ? product.images.map(img => img.src) : [];
            console.log('🖼️ DEBUG: imagesToSend:', imagesToSend);
            
            // Use extension directly with product data
            await window.CrossListerExtension.crosslistToVinted({
              title: product.name,
              name: product.name,
              description: product.description,
              price: vintedPrice.toFixed(2),
              images: imagesToSend,
              sku: product.sku
            });
            
            // Mark as success in bulk queue
            addNotification('success', `Vinted crosslisting started for ${product.name} at £${vintedPrice.toFixed(2)}${vintedMarkup > 0 ? ` (+${vintedMarkup}%)` : ''}`, 'Extension Active');
            
          } catch (error) {
            console.error('Extension error:', error);
            
            // Check if it's a context invalidation error
            if (error.message && (error.message.includes('context invalidated') || error.message.includes('Extension context') || error.message.includes('refresh'))) {
              addNotification('error', 'Extension needs refresh - please reload page', 'Extension Error');
              return;
            }
            
            addNotification('error', `Vinted extension error for ${product.name}`, 'Extension Error');
          }
        } else {
          // Extension not installed
          addNotification('error', `Vinted extension required for ${product.name}`, 'Extension Required');
        }
      } else if (platformKey === 'etsy') {
        // Etsy uses extension directly - no API needed
        console.log('🔍 Checking for Etsy extension...', window.CrossListerExtension);
        
        if (window.CrossListerExtension && window.CrossListerExtension.isInstalled) {
          console.log('✅ Extension detected! Calling crosslistToEtsy...');
          try {
            // Use extension directly with product data
            await window.CrossListerExtension.crosslistToEtsy({
              title: product.name,
              name: product.name,
              description: product.description,
              price: product.price,
              images: product.images ? product.images.map(img => img.src) : [],
              sku: product.sku
            });
            
            // Mark as success in bulk queue
            addNotification('success', `Etsy crosslisting started for ${product.name}`, 'Extension Active');
            
          } catch (error) {
            console.error('Extension error:', error);
            
            // Check if it's a context invalidation error
            if (error.message && (error.message.includes('context invalidated') || error.message.includes('Extension context') || error.message.includes('refresh'))) {
              addNotification('error', 'Extension needs refresh - please reload page', 'Extension Error');
              return;
            }
            
            addNotification('error', `Etsy extension error for ${product.name}`, 'Extension Error');
          }
        } else {
          // Extension not installed
          addNotification('error', `Etsy extension required for ${product.name}`, 'Extension Required');
        }
      } else {
        // eBay uses direct API calls
        response = await apiClient.post(`/api/crosslist/${platformKey}/`, {
          product_id: product.id
        });
        
        if (response.data.success) {
          // Update queue item to success
          setCrosslistQueue(prev => prev.map(item => 
            item.id === queueItem.id 
              ? { ...item, status: 'completed', completedAt: new Date().toISOString() }
              : item
          ));
          
          // Optimistically update the product's listings
          setProducts(prevProducts => prevProducts.map(p => {
            if (p.id === product.id) {
              const updatedListings = { ...p.listings };
              updatedListings[platformKey] = {
                id: response.data.item_id || response.data.listing_id,
                url: response.data.listing_url || '#',
                status: 'active',
                created_at: new Date().toISOString()
              };
              return { ...p, listings: updatedListings };
            }
            return p;
          }));
          
          // Success notification handled by queue status update
          // Refresh products to show updated listings
          fetchProducts();
          fetchAllProducts(true);
        }
      }
    } catch (error) {
      // Update queue item to failed
      setCrosslistQueue(prev => prev.map(item => 
        item.id === queueItem.id 
          ? { ...item, status: 'failed', error: error.response?.data?.error || error.message, failedAt: new Date().toISOString() }
          : item
      ));
      console.error(`Crosslisting to ${platformKey} failed:`, error);
      // Error notification handled by queue status update
    }
  };

  return (
    <div className="container">
      <NotificationSystem 
        notifications={notifications} 
        removeNotification={removeNotification}
        cancelAction={cancelAction}
      />
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        showSuccess={showSuccess}
        showError={showError}
        showWarning={showWarning}
        selectedPolicies={selectedPolicies}
        setSelectedPolicies={setSelectedPolicies}
        platformSettings={platformSettings}
        setPlatformSettings={setPlatformSettings}
        sourcePlatform={sourcePlatform}
        setSourcePlatform={setSourcePlatform}
      />

      <CrosslistModal
        isOpen={crosslistModal.isOpen}
        product={crosslistModal.product}
        bulkProducts={crosslistModal.bulkProducts}
        onClose={() => setCrosslistModal({ isOpen: false, product: null, bulkProducts: null })}
        onCrosslist={handleCrosslistProduct}
        showSuccess={showSuccess}
        showError={showError}
        showWarning={showWarning}
      />

      {showIndividualDeleteModal && productToDelete && (
        <ConfirmationModal
          title="Delete Product"
          message={`Are you sure you want to permanently delete "${productToDelete.name}"?

⚠️ This will:
• Remove the product from your dashboard permanently
• Delist it from all connected platforms (WooCommerce, eBay, Etsy, Vinted)
• Cannot be undone

If you just want to remove from platforms but keep in dashboard, use "Delist from Platforms" instead.`}
          confirmText="Yes, Permanently Delete & Delist"
          onConfirm={handleIndividualDeleteConfirm}
          onClose={() => {
            setShowIndividualDeleteModal(false);
            setProductToDelete(null);
          }}
          isDestructive={true}
        />
      )}

      {showIndividualDelistModal && productToDelist && (
        <UpdateStatusModal
          products={[productToDelist]}
          onClose={() => {
            setShowIndividualDelistModal(false);
            setProductToDelist(null);
          }}
          onConfirm={async (action, platforms) => {
            try {
              setIsPerformingAction(true);
              const response = await apiClient.post("/api/products/bulk_action/", {
                ids: [productToDelist.id],
                action: "delist",
                platforms: platforms
              });

              if (response.data?.message) {
                showSuccess(response.data.message, 'Delist Completed');
                
                // Optimistically update the product status to inactive
                setProducts(prevProducts => prevProducts.map(p => 
                  p.id === productToDelist.id ? { ...p, status: 'inactive' } : p
                ));
                
                fetchProducts();
                fetchAllProducts();
                
                setShowIndividualDelistModal(false);
                setProductToDelist(null);
              } else {
                showError('Delisting failed. Please try again.', 'Delist Failed');
              }
            } catch (error) {
              const errorMessage = error.response?.data?.error || error.message || 'Delisting failed';
              showError(`Delisting failed: ${errorMessage}`, 'Delist Failed');
            } finally {
              setIsPerformingAction(false);
            }
          }}
          isLoading={isPerformingAction}
        />
      )}

      {editingProduct && (
        <ProductDetailModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleSaveProduct}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
          platformSettings={platformSettings}
        />
      )}

      <div className="header">
        <h1>CrossLister Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            ref={syncButtonRef}
            onClick={() => setIsSyncDropdownOpen(!isSyncDropdownOpen)}
            className="button-primary"
            title="Sync products from marketplaces"
          >
            Sync
          </button>
          {/* Development only - remove in production */}
          {process.env.REACT_APP_DEBUG === 'true' && (
            <button
              onClick={handleSaleDetection}
              className="button-secondary"
              title="Check for sales and automatically delist items (Dev Only)"
              disabled={isRunningSaleDetection}
            >
              {isRunningSaleDetection ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '4px'
                  }}></span>
                  Checking...
                </>
              ) : (
                'Sale Detection'
              )}
            </button>
          )}
          <button
            ref={toolsButtonRef}
            onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
            className="button-secondary"
            title="Export, import, and print tools"
          >
            Tools
          </button>
          <button
            onClick={() => setIsSettingsModalOpen(true)}
            className="settings-button"
          >
            Settings
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
          
          {/* Notifications Panel - Moved to far right */}
          <div style={{ position: 'relative', marginLeft: '16px' }}>
            <button
              onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              className="settings-button"
              style={{ 
                position: 'relative'
              }}
              title="View notifications and task queue"
            >
              🔔
              {(newSales.length > 0 || crosslistQueue.filter(item => item && item.product).length > 0) && (
                <span 
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: crosslistQueue.some(item => item.status === 'failed') || newSales.length > 0
                      ? 'var(--color-error)'
                      : crosslistQueue.some(item => item.status === 'processing')
                      ? 'var(--color-primary)'
                      : 'var(--color-success)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    border: '2px solid var(--color-surface)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {newSales.length + crosslistQueue.filter(item => item && item.product).length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
          
      {/* Notification Panel - Right Side Overlay */}
      {showNotificationPanel && (
        <div 
          style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '400px',
                height: '100vh',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--shadow-xl)',
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                transform: showNotificationPanel ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease-in-out'
              }}
            >
              {/* Panel Header */}
              <div style={{ 
                padding: '20px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Notifications</h2>
                <button
                  onClick={() => setShowNotificationPanel(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'var(--color-text-tertiary)',
                    padding: '4px'
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Tab Navigation */}
              <div style={{ 
                display: 'flex',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <button
                  onClick={() => setActiveNotificationTab('queue')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: activeNotificationTab === 'queue' 
                      ? 'var(--color-surface-secondary)' 
                      : 'transparent',
                    borderBottom: activeNotificationTab === 'queue' 
                      ? '2px solid var(--color-primary)' 
                      : '2px solid transparent',
                    color: activeNotificationTab === 'queue' 
                      ? 'var(--color-primary)' 
                      : 'var(--color-text-secondary)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Queue ({crosslistQueue.filter(item => item && item.product).length})
                </button>
                <button
                  onClick={() => setActiveNotificationTab('sales')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    background: activeNotificationTab === 'sales' 
                      ? 'var(--color-surface-secondary)' 
                      : 'transparent',
                    borderBottom: activeNotificationTab === 'sales' 
                      ? '2px solid var(--color-primary)' 
                      : '2px solid transparent',
                    color: activeNotificationTab === 'sales' 
                      ? 'var(--color-primary)' 
                      : 'var(--color-text-secondary)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Sales ({newSales.length})
                </button>
              </div>
              
              {/* Panel Content - Scrollable Area */}
              <div style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ 
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 180px)', // Account for header, tabs, and footer button
                  padding: '16px',
                  paddingBottom: '80px' // Space for sticky button
                }}>
                  {activeNotificationTab === 'queue' && (
                    <div>
                      {crosslistQueue.length === 0 ? (
                        <p style={{ 
                          color: 'var(--color-text-secondary)', 
                          fontSize: '14px', 
                          textAlign: 'center',
                          margin: '40px 0'
                        }}>
                          No items in queue
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {crosslistQueue.filter(item => item && item.product).map((item) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                if (item.action === 'delete') {
                                  // Use the new individual delete confirmation modal
                                  setProductToDelete(item.product);
                                  setShowIndividualDeleteModal(true);
                                  setShowNotificationPanel(false); // Close notifications panel
                                } else {
                                  // Open product detail modal
                                  setEditingProduct(item.product);
                                }
                              }}
                              style={{
                                padding: '12px',
                                backgroundColor: 'var(--color-surface-secondary)',
                                borderRadius: '8px',
                                border: '1px solid var(--color-border)',
                                borderLeft: `4px solid ${
                                  item.status === 'processing' 
                                    ? 'var(--color-primary)'
                                    : item.status === 'completed'
                                    ? 'var(--color-success)'
                                    : 'var(--color-error)'
                                }`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--color-surface-tertiary)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--color-surface-secondary)';
                              }}
                            >
                              {/* Product Image */}
                              <img
                                src={
                                  item.product.images?.[0]?.src ||
                                  "https://placehold.co/60x60/f9fafb/e5e7eb?text=N/A"
                                }
                                alt={item.product.name}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-border)'
                                }}
                                onError={(e) => {
                                  e.target.src = "https://placehold.co/60x60/f9fafb/e5e7eb?text=N/A";
                                }}
                              />
                              
                              {/* Status and Action Info */}
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  fontSize: '12px',
                                  color: 'var(--color-text-secondary)',
                                  marginBottom: '4px',
                                  textTransform: 'capitalize'
                                }}>
                                  {item.action || 'crosslist'} → {item.platform?.charAt(0).toUpperCase() + item.platform?.slice(1)}
                                </div>
                                <div style={{ 
                                  fontSize: '13px',
                                  color: item.status === 'processing' 
                                    ? 'var(--color-primary)'
                                    : item.status === 'completed'
                                    ? 'var(--color-success)'
                                    : 'var(--color-error)',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {item.status === 'processing' && (
                                    <span style={{
                                      display: 'inline-block',
                                      width: '8px',
                                      height: '8px',
                                      border: '1px solid currentColor',
                                      borderTop: '1px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      marginRight: '6px'
                                    }}></span>
                                  )}
                                  {item.status === 'processing' ? 'Processing...' : item.status}
                                </div>
                                {item.error && (
                                  <QueueErrorDisplay error={item.error} />
                                )}
                              </div>
                              
                              {/* Timestamp */}
                              <div style={{ 
                                fontSize: '10px', 
                                color: 'var(--color-text-tertiary)',
                                textAlign: 'right'
                              }}>
                                {formatDate(item.timestamp)}
                              </div>
                            </div>
                          ))}
                        </div>
                    )}
                  </div>
                )}
                
                {activeNotificationTab === 'sales' && (
                  <div>
                    {newSales.length === 0 ? (
                      <p style={{ 
                        color: 'var(--color-text-secondary)', 
                        fontSize: '14px', 
                        textAlign: 'center',
                        margin: '40px 0'
                      }}>
                        No new sales to display
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                          {newSales.filter(sale => sale && typeof sale === 'object').map((sale, index) => (
                            <div
                              key={`sale-${sale.id || sale.sku || index}`}
                              style={{
                                padding: '16px',
                                backgroundColor: 'var(--color-surface-secondary)',
                                borderRadius: '8px',
                                border: '1px solid var(--color-border)',
                                borderLeft: '4px solid var(--color-success)',
                                position: 'relative'
                              }}
                            >
                              <button
                                onClick={() => setNewSales(prev => prev.filter((_, i) => i !== index))}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--color-text-tertiary)',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'var(--color-error)';
                                  e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = 'var(--color-text-tertiary)';
                                }}
                                title="Remove this notification"
                              >
                                ×
                              </button>
                              
                              <div style={{ paddingRight: '24px' }}>
                                <div style={{ 
                                  fontWeight: '600', 
                                  fontSize: '15px',
                                  marginBottom: '8px',
                                  color: 'var(--color-text-primary)'
                                }}>
                                  {sale.productName}
                                </div>
                                <div style={{ 
                                  fontSize: '13px', 
                                  color: 'var(--color-text-secondary)',
                                  marginBottom: '8px'
                                }}>
                                  Platform: {sale.platform} • SKU: {sale.sku}
                                </div>
                                <div style={{ 
                                  fontSize: '14px', 
                                  color: 'var(--color-success)',
                                  fontWeight: '600'
                                }}>
                                  Sold for {formatCurrency(sale.salePrice)}
                                </div>
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: 'var(--color-text-tertiary)',
                                  marginTop: '4px'
                                }}>
                                  {sale.saleDate}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Sticky Clear All Button */}
              <div style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                zIndex: 10
              }}>
                <button
                  onClick={() => {
                    // Clear all notifications
                    setCrosslistQueue([]);
                    setNewSales([]);
                  }}
                  style={{
                    backgroundColor: 'var(--color-danger)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--color-danger-hover)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--color-danger)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  title="Clear all notifications"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
          
      {/* Overlay to close panel */}
      {showNotificationPanel && (
        <div 
          onClick={() => setShowNotificationPanel(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1000
          }}
        />
      )}

      <Navbar
        activeTab={activeTab}
        setActiveTab={debouncedTabSwitch}
        productCounts={productCounts}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        <StatsCard
          title="Today's Sales"
          value={salesStats.today_sales.value}
          change={`${salesStats.today_sales.count} items sold today`}
          changeType="neutral"
          icon=""
          color="#10B981"
        />
        <StatsCard
          title="Monthly Sales"
          value={salesStats.monthly_sales.value}
          change={`${salesStats.monthly_sales.count} items sold this month`}
          changeType="neutral"
          icon=""
          color="#3B82F6"
        />
        <StatsCard
          title="Total Sales"
          value={salesStats.total_sales.value}
          change={`${salesStats.total_sales.count} total items sold`}
          changeType="neutral"
          icon=""
          color="#8B5CF6"
        />
        <StatsCard
          title="Active Listings"
          value={salesStats.product_counts.active}
          change={`${salesStats.product_counts.sold} items sold`}
          changeType="neutral"
          icon=""
          color="#F59E0B"
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-message">
          <div
            className="loading-spinner"
            style={{ marginBottom: "12px" }}
          ></div>
          <p>Loading products...</p>
        </div>
      ) : (
        <>
          {activeTab === "active" && (
            <>
              <ProductDashboard
                products={products}
                allProducts={allProducts}
                onProductsChange={() => {
                  fetchProducts();
                  fetchAllProducts();
                }}
                onEditProduct={setEditingProduct}
                onCrossList={handleCrossList}
                onDelist={handleDelist}
                onMarkAsSold={handleMarkAsSold}
                onMarkAsActive={handleMarkAsActive}
                onDelete={handleDelete}
                onToggleActionsMenu={handleToggleActionsMenu}
                setCrosslistModal={setCrosslistModal}
                activeTab={activeTab}
                searchTerm={searchTerm}
                platformSettings={platformSettings}
                setSearchTerm={setSearchTerm}
                debouncedSearchTerm={debouncedSearchTerm}
                isPerformingAction={isPerformingAction}
                setIsPerformingAction={setIsPerformingAction}
                showError={showError}
                showWarning={showWarning}
                showSuccess={showSuccess}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </>
          )}
          {activeTab === "sold" && (
            <>
              <ProductDashboard
                products={products}
                allProducts={allProducts}
                onProductsChange={() => {
                  fetchProducts();
                  fetchAllProducts();
                }}
                onEditProduct={setEditingProduct}
                onCrossList={handleCrossList}
                onDelist={handleDelist}
                onMarkAsSold={handleMarkAsSold}
                onMarkAsActive={handleMarkAsActive}
                onDelete={handleDelete}
                onToggleActionsMenu={handleToggleActionsMenu}
                setCrosslistModal={setCrosslistModal}
                activeTab={activeTab}
                searchTerm={searchTerm}
                platformSettings={platformSettings}
                setSearchTerm={setSearchTerm}
                debouncedSearchTerm={debouncedSearchTerm}
                isPerformingAction={isPerformingAction}
                setIsPerformingAction={setIsPerformingAction}
                showError={showError}
                showWarning={showWarning}
                showSuccess={showSuccess}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </>
          )}
          {activeTab === "inactive" && (
            <>
              <ProductDashboard
                products={products}
                allProducts={allProducts}
                onProductsChange={() => {
                  fetchProducts();
                  fetchAllProducts();
                }}
                onEditProduct={setEditingProduct}
                onCrossList={handleCrossList}
                onDelist={handleDelist}
                onMarkAsSold={handleMarkAsSold}
                onMarkAsActive={handleMarkAsActive}
                onDelete={handleDelete}
                onToggleActionsMenu={handleToggleActionsMenu}
                setCrosslistModal={setCrosslistModal}
                activeTab={activeTab}
                searchTerm={searchTerm}
                platformSettings={platformSettings}
                setSearchTerm={setSearchTerm}
                debouncedSearchTerm={debouncedSearchTerm}
                isPerformingAction={isPerformingAction}
                setIsPerformingAction={setIsPerformingAction}
                showError={showError}
                showWarning={showWarning}
                showSuccess={showSuccess}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </>
          )}
          {activeTab === "action_required" && (
            <>
              <ProductDashboard
                products={products}
                allProducts={allProducts}
                onProductsChange={() => {
                  fetchProducts();
                  fetchAllProducts();
                }}
                onEditProduct={setEditingProduct}
                onCrossList={handleCrossList}
                onDelist={handleDelist}
                onMarkAsSold={handleMarkAsSold}
                onMarkAsActive={handleMarkAsActive}
                onDelete={handleDelete}
                onToggleActionsMenu={handleToggleActionsMenu}
                setCrosslistModal={setCrosslistModal}
                activeTab={activeTab}
                searchTerm={searchTerm}
                platformSettings={platformSettings}
                setSearchTerm={setSearchTerm}
                debouncedSearchTerm={debouncedSearchTerm}
                isPerformingAction={isPerformingAction}
                setIsPerformingAction={setIsPerformingAction}
                showError={showError}
                showWarning={showWarning}
                showSuccess={showSuccess}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </>
          )}
        </>
      )}

      <SyncDropdown
        isOpen={isSyncDropdownOpen}
        onClose={() => setIsSyncDropdownOpen(false)}
        onWooCommerceSync={handleWooCommerceSync}
        onEbaySync={handleEbaySync}
        onEtsySync={handleEtsySync}
        isPerformingSync={isPerformingSync}
        syncingPlatform={syncingPlatform}
        triggerRef={syncButtonRef}
        showWarning={showWarning}
      />

      <ToolsDropdown
        isOpen={isToolsDropdownOpen}
        onClose={() => setIsToolsDropdownOpen(false)}
        triggerRef={toolsButtonRef}
        filteredProductsLength={allProducts
          .filter(product => product.status === activeTab)
          .filter(product => {
            if (!debouncedSearchTerm) return true;
            const searchLower = debouncedSearchTerm.toLowerCase();
            return (
              (product.name && product.name.toLowerCase().includes(searchLower)) ||
              (product.sku && product.sku.toLowerCase().includes(searchLower))
            );
          }).length}
        isPerformingAction={isPerformingAction}
        onExport={() => {
          const allFilteredProducts = allProducts
            .filter(product => product.status === activeTab)
            .filter(product => {
              if (!debouncedSearchTerm) return true;
              const term = debouncedSearchTerm.toLowerCase();
              return (
                product.name?.toLowerCase().includes(term) ||
                product.sku?.toLowerCase().includes(term)
              );
            });
          exportToCSV(allFilteredProducts, `${activeTab}-products-${new Date().toISOString().split('T')[0]}.csv`, showError);
        }}
        onPrint={() => window.print()}
        onImport={() => setIsImportModalOpen(true)}
        onSaleDetection={handleSaleDetection}
        isRunningSaleDetection={isRunningSaleDetection}
        filteredProductsLength={products.length}
        isPerformingAction={false}
      />

      {isImportModalOpen && (
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImport}
          showError={showError}
        />
      )}

      {/* Global Dropdown Menu */}
      <GlobalDropdownMenu
        isVisible={globalDropdown.isVisible}
        position={globalDropdown.position}
        onClose={handleCloseActionsMenu}
        product={globalDropdown.product}
        activeTab={activeTab}
        onCrossList={handleCrossList}
        onDelist={handleDelist}
        onMarkAsSold={handleMarkAsSold}
        onDelete={handleDelete}
        onMarkAsActive={handleMarkAsActive}
      />
    </div>
  );
}

function Pagination({ 
  currentPage, 
  totalPages, 
  totalItems,
  itemsPerPage,
  onPageChange, 
  onItemsPerPageChange 
}) {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '16px 0',
      borderTop: '1px solid var(--color-gray-200)',
      marginTop: '16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: 'var(--color-gray-600)' }}>
        <span>
          Showing {startItem}-{endItem} of {totalItems} products
        </span>
        
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          style={{ 
            padding: '4px 8px', 
            border: '1px solid var(--color-gray-300)', 
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
          <option value={200}>200 per page</option>
        </select>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="button-secondary"
          style={{ padding: '6px 12px', fontSize: '14px' }}
        >
          Previous
        </button>

        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} style={{ padding: '6px 4px', color: 'var(--color-gray-400)' }}>
              ...
            </span>
          ) : (
            <button
              key={`page-${page}`}
              onClick={() => onPageChange(page)}
              className={currentPage === page ? 'button-primary' : 'button-secondary'}
              style={{ 
                padding: '6px 12px', 
                fontSize: '14px',
                minWidth: '36px'
              }}
            >
              {page}
            </button>
          )
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || totalPages <= 1}
          className="button-secondary"
          style={{ padding: '6px 12px', fontSize: '14px' }}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function LoginForm({ onLogin }) {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const endpoint = showRegister ? "/api/auth/register/" : "/api/auth/login/";

    try {
      const response = await axios.post(`${API_URL}${endpoint}`, formData);
      onLogin(response.data.key);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.error ||
        (showRegister
          ? "Registration failed."
          : "Login failed. Please check your credentials.");
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <h1>{showRegister ? "Create Account" : "Welcome Back"}</h1>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={isSubmitting}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="button-primary"
        >
          {isSubmitting ? (
            <>
              <span
                className="loading-spinner"
                style={{ marginRight: "8px" }}
              ></span>
              {showRegister ? "Creating Account..." : "Signing In..."}
            </>
          ) : showRegister ? (
            "Create Account"
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <button
          type="button"
          onClick={() => {
            setShowRegister(!showRegister);
            setError("");
            setFormData({ username: "", password: "" });
          }}
          style={{
            background: "none",
            border: "none",
            color: "var(--color-primary)",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "14px",
          }}
          disabled={isSubmitting}
        >
          {showRegister
            ? "Already have an account? Sign in"
            : "Don't have an account? Create one"}
        </button>
      </div>
    </div>
  );
}

export default App;
